import { Dayjs } from "dayjs";
import { formatDateParam } from "../components/ETax/dateRangeParams";
import {
  InvoiceWasteResponse,
  InvoiceWasteSummary,
  UpdateReconcileDetailsRequest,
} from "../types/invoice-waste";
import { SaleRouteRequest } from "../types/sale-route.type";
import {
  RevertCancelCheck,
  RevertCancelRequest,
  RevertCancelResult,
} from "../types/revert-cancel.type";
import {
  SpecialOrderCancelRequest,
  SpecialOrderCreateRequest,
  SpecialOrderDeliverRequest,
  SpecialOrderDetail,
  SpecialOrderDocumentSheets,
  SpecialOrderListData,
  SpecialOrderListParams,
  SpecialOrderPriceList,
  SpecialOrderUpdateRequest,
  SpecialRouteCloseDayRequest,
  SpecialRouteCloseDayResult,
  SpecialRoutePending,
} from "../types/special-order.type";
import { UserCreateRequest, UserUpdateRequest } from "../types/user.type";
import { CompanySalesReport, CompanySalesReportParams } from "../types/companySalesReport.type";
import { VehicleRequest } from "../types/vehicle.type";
import {
  CreateBatchRequest,
  ETaxCreateBatchResult,
  ETaxDocumentFilter,
  ETaxNoteBatchRequest,
  ETaxNoteListResponse,
  ETaxSummaryFilter,
} from "../types/e-tax.type";
import { UpdateEtaxAutoSendSettingsRequest } from "../types/etax-auto-send.type";
import {
  CompanyProfile,
  CompanyProfileUpdateRequest,
} from "../types/company-profile.type";
import {
  CustomerCreateRequest,
  CustomerUpdateRequest,
  CustomerExportParams,
  CustomerListParams,
  CustomerResponse,
  CustomerStatus,
  UpdateCustomerGroupPrintSetsRequest,
  UpdateCustomerPrintSettingsRequest,
} from "../types/customer.type";
import {
  SystemSetting,
  SystemSettingHistoryResponse,
  SystemSettingListResponse,
  UpdateSystemSettingRequest,
} from "../types/system-setting.type";
import {
  ApiResponse,
  ExpressDataExportParams,
  ExpressDataListResponse,
  ExpressDataParams,
} from "../types/express-data.type";
import {
  QrAnalyticsSummaryResponse,
  QrAnalyticsTrendResponse,
  QrShopRankResponse,
  QrShopSortBy,
  QrShopSortDir,
  QrTrendGroupBy,
} from "../types/qr-analytics.type";
import {
  VisitFrequencyCustomerParams,
  VisitFrequencyCustomerResponse,
  VisitFrequencySummaryParams,
  VisitFrequencySummaryResponse,
} from "../types/visit-frequency.type";
import {
  PalletBranchSummary,
  PalletRouteSummary,
  PalletRouteSummaryParams,
  PalletSummaryDateRange,
  PalletTripNoteResult,
  PalletTripSummary,
  PalletTripSummaryParams,
} from "../types/pallet-summary.type";
import {
  WastePercentReport,
  WastePercentReportParams,
} from "../types/waste-percent-report.type";
import {
  InactiveStoreExportParams,
  InactiveStoreFilterParams,
  InactiveStoreListParams,
  InactiveStorePageResponse,
  InactiveStoreSummaryParams,
  InactiveStoreSummaryResponse,
} from "../types/inactive-stores.type";
import { CustomerGroupExportParams } from "../types/sales-report-export.type";
import {
  CustomerGroupExportSettings,
  CustomerGroupWithExportSettings,
} from "../types/customer-group-export.type";
import { Gb1SaleReportExportParams } from "../types/gb1-sale-report.type";
import { VanLoadUploadResponse } from "../types/van-load.type";
import {
  NoRunDayListResponse,
  NoRunDayParams,
} from "../types/no-run-day.type";
import {
  PathwayUploadConfirmResponse,
  PathwayUploadPreviewResponse,
} from "../types/pathway-upload.type";
import {
  PurchaseOrderChain,
  PurchaseOrderCustomerCandidatesData,
  PurchaseOrderDetail,
  PurchaseOrderImportBranchChoice,
  PurchaseOrderImportConfirmData,
  PurchaseOrderImportPreviewData,
  PurchaseOrderListData,
  PurchaseOrderListFilter,
  PurchaseOrderListItem,
  PurchaseOrderListParams,
  PurchaseOrderPublishData,
  PurchaseOrderPublishRequest,
  PurchaseOrderReturnData,
  PurchaseOrderReturnRequest,
  PurchaseOrderSetBranchData,
  PurchaseOrderSetBranchRequest,
  PurchaseOrderUnpublishData,
  PurchaseOrderUnpublishRequest,
  PurchaseOrderWeeklySummaryData,
  PurchaseOrderWeeklySummaryParams,
} from "../types/purchase-order.type";
import { DailyGridResponse } from "../types/sale-daily-grid.type";
import {
  TransactionDetailWithItems,
  TransactionHistoryResponse,
} from "../types/sale.type";
import {
  CreditDebitNoteCancelRequest,
  CreditDebitNoteCreateRequest,
  CreditDebitNoteCreateResponse,
  CreditDebitNoteDetail,
  CreditDebitNoteExportParams,
  CreditDebitNoteFilterParams,
  CreditDebitNoteListData,
  CreditDebitNoteListParams,
  CreditDebitNoteOriginalBill,
} from "../types/credit-debit-note.type";
import {
  DashboardRouteLive,
  RouteLiveStockItem,
} from "../types/dashboard.type";
import {
  CreateReprintAuthRequest,
  ReprintAuthListParams,
} from "../types/reprintAuth.type";
import { ReprintRequestListParams } from "../types/reprintRequest.type";
import {
  CreateTransferNotificationRequest,
  PaginatedBranchDaySummary,
  PaginatedSaleDeposit,
  SaleDepositBranchSummaryFilter,
  SaleDepositBulkReviewResult,
  SaleDepositCurrentResponse,
  SaleDepositResponse,
  SaleDepositReportFilter,
  SaleDepositSlipMetadata,
  UpdateSaleDepositExpensesRequest,
} from "../types/sale-deposit.type";
import {
  CloseSaleCenterRequest,
  SaleCenterClosingSummary,
  SendBackSaleCenterRequest,
} from "../types/sale-center-closing.type";
import {
  CreateSaleDepositEditRequestRequest,
  PaginatedSaleDepositEditRequest,
  ReviewSaleDepositEditRequestRequest,
  SaleDepositEditRequest,
  SaleDepositEditRequestFilter,
  SaleDepositEditRequestPendingCount,
} from "../types/sale-deposit-edit-request.type";
import {
  FormulaScorerPreviewRequest,
  FormulaScorerPreviewResponse,
  FormulaDecisionRequest,
  FormulaDecisionResult,
} from "../types/formula-scorer.type";
import {
  ProductCategory,
  ProductCategoryDeleteConflict,
  ProductCategoryWriteRequest,
  ProductCreateRequest,
  ProductDuplicateRequest,
  ProductFacePriceUsage,
  ProductStatus,
  ProductUpdateRequest,
} from "../types/product.type";
import {
  ProductCost,
  ProductCostList,
  ProductCostUpdateRequest,
} from "../types/product-cost.type";
import {
  PriceBookCreateRequest,
  PriceBookDeleteConflict,
  PriceBookNextCode,
  PriceBookStatus,
  PriceBookType,
  PriceBookUpdateRequest,
  PricebookBranches,
  PricebookBranchesUpdateRequest,
  PricebookBranchWithShops,
  PricebookApplyRequest,
  PricebookDraftDiff,
  PricebookHistoryPage,
  PriceBookResponse,
  PricebookItemInput,
  PricebookItemPreviewRequest,
  PricebookItemPreviewResponse,
  PricebookPeriod,
  PricebookPeriodCreateRequest,
  PricebookPeriodOverviewPage,
  PricebookPeriodPage,
  PricebookPeriodStatus,
  PricebookPeriodUpdateRequest,
  PricebookSchedule,
  PricebookSchedulePage,
} from "../types/price-book.type";
import {
  DemandAdjustment,
  DemandAdjustmentListData,
  DemandAdjustmentListParams,
  DemandAdjustmentPreview,
  DemandAdjustmentPreviewRequest,
  DemandAdjustmentWriteRequest,
} from "../types/demand-factor.type";
import {
  RouteCreateRequest,
  RouteManagementList,
  RouteUpdateRequest,
  RouteUsageCounts,
} from "../types/route.type";
import {
  BdcAdjustPreview,
  BdcAdjustPreviewRequest,
  BdcBranch,
  BdcBranchUpsertRequest,
  BdcDc,
  BdcDcUpdateRequest,
  BdcDeliverySaveRequest,
  BdcDeliverySaveResponse,
  BdcDocSequence,
  BdcDocSequenceHistoryData,
  BdcDocumentListParams,
  BdcDocumentRenderRequest,
  BdcDocumentShipment,
  BdcDispatchDay,
  BdcDispatchNote,
  BdcDispatchPrintRequest,
  BdcDispatchRecordRequest,
  BdcImportConfirmData,
  BdcImportPreviewData,
  BdcInvoiceActionResponse,
  BdcInvoiceDetail,
  BdcInvoiceListData,
  BdcInvoiceListParams,
  BdcListShape,
  BdcPriceCheck,
  BdcQtyAdjustmentListData,
  BdcShipmentDetail,
  BdcShipmentExportType,
  BdcShipmentFilter,
  BdcShipmentListData,
  BdcShipmentListParams,
  BdcShipmentStatusResponse,
  BdcSku,
  BdcSkuTray,
  BdcSkuUpdateRequest,
  BdcStockDay,
  BdcStockDayCloseRequest,
  BdcStockDaySaveRequest,
  BdcStockReport,
  BdcStoreListData,
  BdcStoreListParams,
} from "../types/bdc.type";
import { isAxiosError } from "axios";
import axios from "./axios";

// The API answers a refused pricebook, product-category or route delete with this status.
const HTTP_CONFLICT = 409;
// ...and a shop it refuses to put on a pricebook with this one.
const HTTP_UNPROCESSABLE_ENTITY = 422;

export const getListUser = (
  page: number,
  limit: number,
  branch: string,
  search?: string,
) => {
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
  return axios.get(
    `/users/list?limit=${limit}&page=${page}&branch=${branch}${searchParam}`,
  );
};

export const getUser = (id: string) => {
  return axios.get(`/users/${id}`);
};

export const getUserRole = (id: string) => {
  return axios.get(`/users/${id}/roles`);
};

const removeEmpty = <T extends object>(obj: T) =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v !== "" && v !== undefined),
  );

export const createUser = (user: UserCreateRequest) => {
  return axios.post(`/users`, removeEmpty(user), {
    meta: { successMsg: "สร้างผู้ใช้สำเร็จ" },
  });
};

export const editUser = (id: string, user: UserUpdateRequest) => {
  return axios.put(`/users/${id}`, removeEmpty(user), {
    meta: { successMsg: "แก้ไขผู้ใช้สำเร็จ" },
  });
};

export const resetUsernamePasswordUser = (id: string) => {
  return axios.post(`/users/${id}/reset-password`, undefined, {
    meta: { successMsg: "รีเซ็ตรหัสผ่านผู้ใช้สำเร็จ" },
  });
};

// The user list shows this action to MASTER only (UI gate; the API stays ADMIN + MASTER).
export const deleteUser = (id: string) => {
  return axios.delete(`/users/${id}`, {
    meta: { successMsg: "ลบผู้ใช้สำเร็จ" },
  });
};

export const setUserEnabled = (id: string, enabled: boolean) => {
  return axios.patch(
    `/users/${id}/enabled`,
    { enabled },
    {
      meta: {
        successMsg: enabled ? "เปิดใช้งานผู้ใช้สำเร็จ" : "ปิดใช้งานผู้ใช้สำเร็จ",
      },
    },
  );
};

/**
 * Vehicles of one branch. `branchUuid` is honoured for MASTER / ADMIN only; everyone else gets the
 * branch in their token, so they call it without one.
 */
export const getVehicles = (branchUuid?: string) => {
  const query = branchUuid
    ? `?${new URLSearchParams({ branch_uuid: branchUuid }).toString()}`
    : "";
  return axios.get(`/vehicles${query}`);
};

export const createVehicle = (vehicle: VehicleRequest, branchUuid?: string) => {
  return axios.post(
    `/vehicles`,
    { ...vehicle, branch_uuid: branchUuid },
    { meta: { successMsg: "เพิ่มรถสำเร็จ" } },
  );
};

export const updateVehicle = (uuid: string, vehicle: VehicleRequest) => {
  return axios.patch(`/vehicles/${uuid}`, vehicle, {
    meta: { successMsg: "แก้ไขรถสำเร็จ" },
  });
};

export const getAllRole = () => {
  return axios.get(`/roles`);
};

export const getAllBranch = () => {
  return axios.get(`/branches`);
};

export const getCustomerGroups = () => {
  return axios.get(`/customer-groups`);
};

export const updateCustomerGroupPrintSets = (
  uuid: string,
  body: UpdateCustomerGroupPrintSetsRequest,
) => {
  return axios.patch(`/customer-groups/${encodeURIComponent(uuid)}`, body, {
    meta: { successMsg: "บันทึกชุดพิมพ์เริ่มต้นของหมวดลูกค้าแล้ว" },
  });
};

export const getChannels = () => {
  return axios.get(`/channels`);
};

// Shared by the list and its export so the file can never be filtered differently
// from the table it was downloaded from.
const buildCustomerFilterQuery = (params: CustomerExportParams) => {
  const query = new URLSearchParams();
  if (params.name) query.append("name", params.name);
  if (params.customer_code) query.append("customer_code", params.customer_code);
  if (params.route_code) query.append("route_code", params.route_code);
  if (params.branch_uuid) query.append("branch_uuid", params.branch_uuid);
  if (params.status) query.append("status", params.status);
  if (params.bill_type) query.append("bill_type", params.bill_type);
  if (params.etax_channel) query.append("etax_channel", params.etax_channel);
  if (params.sort_by) query.append("sort_by", params.sort_by);
  if (params.sort_order) query.append("sort_order", params.sort_order);
  return query;
};

export const getListCustomer = (params: CustomerListParams) => {
  const query = buildCustomerFilterQuery(params);
  query.append("page", String(params.page));
  query.append("limit", String(params.limit));
  return axios.get(`/customers?${query.toString()}`);
};

// Customer codes are unique only per branch, so one code can list a row per branch.
const CUSTOMER_CODE_LOOKUP_LIMIT = 100;

// Whether a bill's shop is currently an e-Tax shop: the active customer with the bill's code
// in the bill's branch. False when none matches.
export const getCustomerEtaxEnabled = async (
  customerCode: string,
  companyId: string,
): Promise<boolean> => {
  const {
    data: { data },
  } = await getListCustomer({
    page: 1,
    limit: CUSTOMER_CODE_LOOKUP_LIMIT,
    customer_code: customerCode,
  });
  const rows: CustomerResponse[] = data?.rows ?? [];
  return rows.some(
    (row) =>
      row.customer_code === customerCode &&
      row.company_id === companyId &&
      row.etax_enabled === true,
  );
};

export const exportCustomers = (params: CustomerExportParams) => {
  const query = buildCustomerFilterQuery(params);
  return axios.get<Blob>(`/customers/export?${query.toString()}`, {
    responseType: "blob",
    meta: { suppressForbiddenRedirect: true },
  });
};

export const createCustomer = (data: CustomerCreateRequest) => {
  return axios.post(`/customers`, data, {
    meta: { successMsg: "สร้างลูกค้าสำเร็จ" },
  });
};

export const updateCustomer = (id: string, data: CustomerUpdateRequest) => {
  return axios.patch(`/customers/${id}`, data, {
    meta: { successMsg: "แก้ไขลูกค้าสำเร็จ" },
  });
};

/**
 * ADMIN-only bill-footer discount. Separate from `updateCustomer` on purpose: the
 * generic PATCH drops this field for everyone who is not an admin, and this route
 * is logged on its own so "who changed a shop's discount, and when" is answerable.
 * `null` clears the agreement.
 */
export const updateCustomerDiscount = (
  id: string,
  discount_percent: number | null,
) => {
  return axios.patch(
    `/customers/${id}/discount`,
    { discount_percent },
    {
      meta: {
        successMsg:
          discount_percent == null
            ? "ยกเลิกส่วนลดท้ายบิลสำเร็จ"
            : "แก้ไขส่วนลดท้ายบิลสำเร็จ",
      },
    },
  );
};

export const updateCustomerStatus = (id: string, status: CustomerStatus) => {
  return axios.patch(
    `/customers/${id}/status`,
    { status },
    {
      meta: { successMsg: "อัพเดทสถานะลูกค้าสำเร็จ" },
    },
  );
};

/**
 * Extra filters for GET /products/list. `search` is the box every product screen
 * drives: the API matches it against the name, the code and the barcode.
 */
export interface ProductListFilters {
  brand?: string;
  category_code?: string;
  status?: ProductStatus;
  search?: string;
  allow_export?: boolean;
}

export const getListProduct = (
  page: number,
  limit = 10,
  filters: ProductListFilters = {},
) => {
  // URLSearchParams percent-encodes every value, so Thai names, spaces and "&"
  // survive the round trip.
  const query = new URLSearchParams();
  query.append("limit", String(limit));
  query.append("page", String(page));
  if (filters.brand) query.append("brand", filters.brand);
  if (filters.category_code)
    query.append("category_code", filters.category_code);
  if (filters.status) query.append("status", filters.status);
  if (filters.search) query.append("search", filters.search);
  if (filters.allow_export !== undefined)
    query.append("allow_export", String(filters.allow_export));
  return axios.get(`/products/list?${query.toString()}`);
};

export const createProduct = (data: ProductCreateRequest) => {
  return axios.post(`/products`, data, {
    meta: { successMsg: "สร้างสินค้าสำเร็จ", suppressForbiddenRedirect: true },
  });
};

export const updateProduct = (uuid: string, data: ProductUpdateRequest) => {
  return axios.patch(`/products/${encodeURIComponent(uuid)}`, data, {
    meta: { successMsg: "แก้ไขสินค้าสำเร็จ", suppressForbiddenRedirect: true },
  });
};

/**
 * The pricebooks (all but the face-price one) whose line for the product sits at its current
 * ราคาหน้าซอง, priced again at `price` — what the product form offers to move along with a new
 * face price.
 */
export const getProductFacePriceUsage = (uuid: string, price: number) => {
  const query = new URLSearchParams({ price: String(price) });
  return axios.get<ApiResponse<ProductFacePriceUsage>>(
    `/products/${encodeURIComponent(uuid)}/face-price-usage?${query.toString()}`,
    { meta: { suppressForbiddenRedirect: true } },
  );
};

/**
 * Copy a product. `overrides` carries the values the copy should differ by; anything it
 * omits is taken from the source. The copy's `code` is generated by the API and cannot
 * be chosen, and a `name` already in use is suffixed rather than rejected.
 */
export const duplicateProduct = (
  uuid: string,
  overrides?: ProductDuplicateRequest,
) => {
  return axios.post(
    `/products/${encodeURIComponent(uuid)}/duplicate`,
    overrides,
    {
      meta: {
        successMsg: "ทำสำเนาสินค้าสำเร็จ",
        suppressForbiddenRedirect: true,
      },
    },
  );
};

/**
 * Deletes a product from head office and every branch. Nothing blocks it: bills and reports
 * already issued are snapshots, so the product can go even after it has been sold.
 */
export const deleteProduct = (uuid: string) => {
  return axios.delete(`/products/${encodeURIComponent(uuid)}`, {
    meta: { successMsg: "ลบสินค้าสำเร็จ", suppressForbiddenRedirect: true },
  });
};

const DISABLED_PRODUCT_STATUS: ProductStatus = "disable";

/** Switches a product off without touching anything else on it — the undoable delete. */
export const deactivateProduct = (uuid: string) => {
  return axios.patch(
    `/products/${encodeURIComponent(uuid)}/status`,
    { status: DISABLED_PRODUCT_STATUS },
    {
      meta: { successMsg: "ปิดสินค้าสำเร็จ", suppressForbiddenRedirect: true },
    },
  );
};

/**
 * Multipart upload of the product's two image slots. Either slot may be sent
 * alone; the API answers with the stored { thumbnail, picture } paths.
 */
export const uploadProductImages = (
  uuid: string,
  images: { thumbnail?: File; picture?: File },
) => {
  const formData = new FormData();
  if (images.thumbnail) formData.append("thumbnail", images.thumbnail);
  if (images.picture) formData.append("picture", images.picture);
  return axios.post(`/products/${encodeURIComponent(uuid)}/images`, formData, {
    meta: {
      successMsg: "อัปโหลดรูปภาพสินค้าสำเร็จ",
      suppressForbiddenRedirect: true,
    },
  });
};

export const getProductCategories = (page = 1, limit = 100) => {
  const query = new URLSearchParams();
  query.append("limit", String(limit));
  query.append("page", String(page));
  return axios.get(`/product-categories?${query.toString()}`);
};

/**
 * Creates a หมวดสินค้า and resolves with the stored row, whose `code` the API generated.
 *
 * The error toast is suppressed because the only expected failure is a name the list
 * already holds, and that reads as a validation message on the field the user typed
 * rather than as a toast away from the dialog.
 */
export const createProductCategory = (data: ProductCategoryWriteRequest) => {
  return axios.post<ApiResponse<ProductCategory>>(`/product-categories`, data, {
    meta: {
      successMsg: "สร้างหมวดหมู่สินค้าสำเร็จ",
      suppressForbiddenRedirect: true,
      suppressErrorToast: true,
    },
  });
};

/** Renames a หมวดสินค้า; like create, a failure is shown on the field instead of a toast. */
export const updateProductCategory = (
  uuid: string,
  data: ProductCategoryWriteRequest,
) => {
  return axios.patch(`/product-categories/${encodeURIComponent(uuid)}`, data, {
    meta: {
      successMsg: "แก้ไขหมวดหมู่สินค้าสำเร็จ",
      suppressForbiddenRedirect: true,
      suppressErrorToast: true,
    },
  });
};

/**
 * Deletes a หมวดสินค้า. The API refuses (409) one that products still use; the category
 * dialog words that refusal with the count `readProductCategoryDeleteConflict` reads, so
 * the error toast is suppressed and the dialog reports every failure itself.
 */
export const deleteProductCategory = (uuid: string) => {
  return axios.delete(`/product-categories/${encodeURIComponent(uuid)}`, {
    meta: {
      successMsg: "ลบหมวดหมู่สินค้าสำเร็จ",
      suppressForbiddenRedirect: true,
      suppressErrorToast: true,
    },
  });
};

/** Detail of a 409 raised by `deleteProductCategory`, or null for any other failure. */
export const readProductCategoryDeleteConflict = (
  error: unknown,
): ProductCategoryDeleteConflict | null => {
  if (!isAxiosError<{ error?: ProductCategoryDeleteConflict }>(error)) {
    return null;
  }
  if (error.response?.status !== HTTP_CONFLICT) return null;
  return error.response.data?.error ?? null;
};

/** Head-office products with their unit costs; `search` matches the product code or name. */
export const getProductCosts = (
  page: number,
  limit: number,
  search?: string,
) => {
  const query = new URLSearchParams();
  query.append("limit", String(limit));
  query.append("page", String(page));
  if (search) query.append("search", search);
  return axios.get<ApiResponse<ProductCostList>>(
    `/product-costs?${query.toString()}`,
  );
};

/** Sets a product's unit cost; the API creates the cost row on the first save. */
export const updateProductCost = (
  productCode: string,
  data: ProductCostUpdateRequest,
) => {
  return axios.put<ApiResponse<ProductCost>>(
    `/product-costs/${encodeURIComponent(productCode)}`,
    data,
    {
      meta: {
        successMsg: "แก้ไขต้นทุนสินค้าสำเร็จ",
        suppressForbiddenRedirect: true,
      },
    },
  );
};

export interface TransactionListParams {
  page: number;
  limit?: number;
  transaction_type?: "invoice" | "cashsale";
  status?: string;
  transaction_date?: string;
  routeCode?: string;
  companyId?: string;
  taxId?: string;
  customerName?: string;
  customerCode?: string;
  emp_code?: string;
  warehouse?: string;
  /** Settlement channel — mirrors `payment_type_enum`. */
  paymentType?: string;
  /** Only bills whose replacement carries a GPS flag other than OK. */
  replaceFlagged?: boolean;
  /** Bill number; the API matches it as a case-insensitive prefix. */
  transactionNo?: string;
}

export const getListTransactions = ({
  page,
  limit = 10,
  transaction_type,
  status,
  transaction_date,
  routeCode,
  companyId,
  taxId,
  customerName,
  customerCode,
  emp_code,
  warehouse,
  paymentType,
  replaceFlagged,
  transactionNo,
}: TransactionListParams) => {
  const params = new URLSearchParams();

  params.append("page", page.toString());
  params.append("limit", limit.toString());

  if (transaction_type) params.append("transaction_type", transaction_type);
  if (status && status !== "all") params.append("status", status);
  if (paymentType && paymentType !== "all")
    params.append("payment_type", paymentType);
  if (transaction_date) params.append("transaction_date", transaction_date);
  if (transactionNo) params.append("transaction_no", transactionNo);
  if (companyId && companyId !== "all") params.append("company_id", companyId);
  if (taxId) params.append("inv_taxid", taxId);
  if (customerName) params.append("customer_name", customerName);
  if (customerCode) params.append("customer_code", customerCode);
  if (emp_code) params.append("emp_code", emp_code);
  if (warehouse) params.append("warehouse", warehouse);
  if (routeCode && routeCode !== "all")
    params.append("route_code", routeCode.toString());
  if (replaceFlagged) params.append("replace_flagged", "true");

  return axios.get(`/transactions?${params.toString()}`);
};

export const requestCancelTransaction = (
  uuid: string,
  coords?: { cancel_lat: number; cancel_lon: number },
) => {
  return axios.post(`/transactions/${uuid}/request-cancel`, coords ?? {}, {
    meta: { successMsg: "ส่งคำขอยกเลิกบิลสำเร็จ" },
  });
};

export const cancelTransaction = (uuid: string) => {
  return axios.post(`/transactions/${uuid}/cancel`, undefined, {
    meta: { successMsg: "ยกเลิกบิลสำเร็จ" },
  });
};

export const rejectRequestCancelTransaction = (uuid: string, reason?: string) => {
  const body = reason ? { reason } : {};
  return axios.post(`/transactions/${uuid}/reject-request-cancel`, body, {
    meta: { successMsg: "ปฏิเสธคำขอยกเลิกสำเร็จ" },
  });
};

/**
 * Master-only, read-only: whether a cancelled bill can be restored now, and what
 * blocks or warns if not. The dialog shows the result itself, so no toast.
 */
export const getRevertCancelCheck = (uuid: string) => {
  return axios.get<ApiResponse<RevertCancelCheck>>(
    `/transactions/${encodeURIComponent(uuid)}/revert-cancel/check`,
    { meta: { suppressForbiddenRedirect: true } },
  );
};

/**
 * Master-only: restore a cancelled bill to its status before cancellation. A 409 means
 * the check result changed (new blocker, or unacknowledged warnings); the caller
 * re-runs `getRevertCancelCheck`.
 */
export const revertCancelTransaction = (
  uuid: string,
  data: RevertCancelRequest,
) => {
  return axios.post<ApiResponse<RevertCancelResult>>(
    `/transactions/${encodeURIComponent(uuid)}/revert-cancel`,
    data,
    {
      meta: {
        successMsg: "กู้คืนบิลสำเร็จ",
        suppressForbiddenRedirect: true,
      },
    },
  );
};

/**
 * Correct how a bill was paid. Either field may be sent alone; the API rejects a body
 * carrying neither.
 *
 * This APPENDS a new version of the bill rather than overwriting it — the previous
 * values stay readable through the bill's version history.
 *
 * The API refuses (409) outside the edit window: the bill's own business day, and only
 * until the back office marks the day's deposit ตรวจแล้ว. Those messages come back
 * through the shared axios error interceptor.
 */
export const updateTransactionPayment = (
  uuid: string,
  body: {
    payment_type?: string;
    payment_status?: "paid" | "pending";
    /**
     * Which settled QR payment this bill was really paid by.
     *
     * Required by the API when correcting a bill ONTO QR: back office cannot raise a QR
     * after the fact, so the only honest meaning of that correction is "*this* existing
     * QR settled it". Sending the channel alone is rejected.
     */
    qr_payment_uuid?: string;
    /**
     * What actually landed, when it is less than the bill.
     *
     * Accepted on cash and bank-transfer bills only, and only within a baht of the total —
     * the API refuses anything else. Omitting it leaves whatever the bill already carries
     * untouched, which is why the dialog sends it only when someone types a figure.
     */
    received_amount?: number;
  },
) => {
  return axios.patch(`/transactions/${uuid}/payment`, body, {
    meta: { successMsg: "อัพเดทข้อมูลการชำระเงินสำเร็จ" },
  });
};

/**
 * This bill's most recent QR plus whether any of its QRs closed the sale —
 * `{ payment, counter_closed }`. `payment` is null when no QR ever settled it.
 *
 * `counter_closed` is answered by the server rather than derived from `payment.status`,
 * because it cannot be derived from it: back office can reissue a code for a pay-later bill
 * whose first one expired, which leaves a settled QR behind a live PENDING one, so the
 * newest row reads "open" on a sale closed days ago.
 *
 * The toast is suppressed because the caller renders the outcome inline: a bill with no
 * QR is the ordinary answer for most bills, not a failure worth interrupting for.
 */
export const getQrPaymentByBill = (billUuid: string) => {
  return axios.get(`/qr-payments/bill/${billUuid}`, {
    meta: { suppressErrorToast: true },
  });
};

/** The bills behind a deposit row's outstanding-QR figure, each with the QR to chase. */
export const getOutstandingQrBills = (depositUuid: string) => {
  return axios.get(`/sale-deposits/${depositUuid}/outstanding-qr`);
};

/** The bills behind a deposit row's uncollected-cash figure. Read-only: there is no third
 *  party to ask about these, so the list is the whole of what can be known. */
export const getUncollectedCashBills = (depositUuid: string) => {
  return axios.get(`/sale-deposits/${depositUuid}/outstanding-cash`);
};

/** Confirmed payments no bill claims — the candidates for a correction onto QR. */
export const getUnclaimedQrPayments = (params?: {
  page?: number;
  limit?: number;
}) => {
  const search = new URLSearchParams();
  search.append("page", String(params?.page ?? 1));
  search.append("limit", String(params?.limit ?? 50));
  return axios.get(`/qr-payments/unclaimed?${search.toString()}`);
};

/**
 * The QR Transaction list — every QR the caller may see.
 *
 * `branch_uuid` only narrows anything for head office; the server pins everyone else to
 * their own branch and ignores it, so sending it is harmless but pointless.
 *
 * Empty filters are omitted rather than sent blank: the server validates what it is given,
 * and an empty string is a value, not an absence.
 */
export interface QrPaymentFilterParams {
  status?: string;
  queue?: string;
  branch_uuid?: string;
  created_date?: string;
}

const buildQrPaymentFilterQuery = (params?: QrPaymentFilterParams) => {
  const search = new URLSearchParams();
  for (const key of ["status", "queue", "branch_uuid", "created_date"] as const) {
    const value = params?.[key];
    if (value) search.append(key, value);
  }
  return search;
};

export const getQrPayments = (
  params?: QrPaymentFilterParams & { page?: number; limit?: number },
) => {
  const search = buildQrPaymentFilterQuery(params);
  search.append("page", String(params?.page ?? 1));
  search.append("limit", String(params?.limit ?? 20));
  return axios.get(`/qr-payments?${search.toString()}`);
};

export const exportQrPayments = (params?: QrPaymentFilterParams) => {
  const search = buildQrPaymentFilterQuery(params);
  return axios.get<Blob>(`/qr-payments/export?${search.toString()}`, {
    responseType: "blob",
    meta: { suppressForbiddenRedirect: true },
  });
};

/**
 * Money ordered back that has not gone back yet.
 *
 * The reconciliation figure, and the reason this endpoint exists at all: a cancelled bill
 * leaves เงินโอน the moment it is cancelled, but its money only leaves the bank when
 * accounting get round to transferring it. Between those two moments our takings for the day
 * read LOWER than the statement by exactly this total, and without it nobody can say why.
 *
 * Deliberately not date-filtered. A payment ordered back three weeks ago and still sitting
 * there is the row accounting are hunting for, not one to age out of the report.
 */
export const getQrRefundOutstanding = (branchUuid?: string) => {
  const search = new URLSearchParams();
  if (branchUuid) search.append("branch_uuid", branchUuid);
  return axios.get(`/qr-payments/refund-outstanding?${search.toString()}`);
};

/**
 * Order this QR's money sent back to the payer.
 *
 * Asks the bank for nothing. The platform stopped reversing payments itself — accounting
 * refund them in the bank's own system — so this writes a work order and no more. It is
 * one-way: the server refuses a second order on the same row, and refuses to attach the money
 * to any bill once it is set, because the same baht must not be both refunded and counted.
 *
 * No success toast from the interceptor: the caller reloads the list so the row's own state
 * is the confirmation. A toast saying "done" beside a row that still reads เงินเข้าแล้ว is
 * worse than no toast at all.
 */
export const requestQrRefund = (uuid: string, reason?: string) => {
  // The confirm dialog owns the failure message. A toast would appear over the dialog that
  // is still open, above a list of near-identical rows, and would not say which row it meant.
  return axios.post(
    `/qr-payments/${uuid}/refund-request`,
    { reason },
    { meta: { suppressErrorToast: true, suppressForbiddenRedirect: true } },
  );
};

/**
 * Record that accounting has actually sent the money back.
 *
 * The terminal act, and the reason `reference` is not optional: nothing in the platform ever
 * re-reads a refunded row, so the bank's own reference for the transfer is the only evidence
 * that will ever exist that the money moved. Confirming without one would leave a row that
 * says money went back with nothing behind the claim.
 */
export const confirmQrRefund = (uuid: string, reference: string, reason?: string) => {
  return axios.post(
    `/qr-payments/${uuid}/refund-confirm`,
    { reference, reason },
    { meta: { suppressErrorToast: true, suppressForbiddenRedirect: true } },
  );
};

/**
 * Raise a fresh, payable QR for a sale that was closed on a promise of a transfer.
 *
 * The customer was shown a code at the door and it has since expired, so there is nothing
 * they can pay with any more. This mints a new one against the same bill and the same money.
 * The settled row is untouched — it carries the reason the sale was let go unpaid.
 *
 * Errors are suppressed for the dialog to render inline: the reasons the server refuses this
 * (still payable, bill already paid) are things the operator must read beside the row they
 * pressed, not in a toast floating over a list of near-identical rows.
 */
export const reissueQrPayment = (uuid: string) => {
  return axios.post(`/qr-payments/${uuid}/reissue`, undefined, {
    meta: { suppressErrorToast: true, suppressForbiddenRedirect: true },
  });
};

/** Ask the bank whether this QR has been paid. Writes the answer back to the record. */
export const verifyQrPayment = (uuid: string) => {
  return axios.post(`/qr-payments/${uuid}/verify`, undefined, {
    meta: { successMsg: "ตรวจสอบกับธนาคารแล้ว" },
  });
};

/**
 * The single source of truth for what a failed receipt download says to the
 * user. The interceptor resolves these by status; callers must not push a
 * message of their own on top, or the more specific one is lost — see the
 * note in `usePdfDownload`.
 *
 * 404 is the bill genuinely having no receipt behind it (a van-sale synced
 * bill). 502 is the backend having tried to render one on the spot and failed
 * — a DocGen or storage fault, worth retrying, unlike a 404.
 */
export const RECEIPT_DOWNLOAD_ERROR_MSG_BY_STATUS: Record<number, string> = {
  404: "ไม่พบไฟล์ใบเสร็จสำหรับรายการนี้",
  502: "สร้างไฟล์ใบเสร็จไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
};

export const downloadTransactionReceipt = (uuid: string) => {
  return axios.get(`/transactions/${uuid}/receipts/download`, {
    responseType: "blob",
    meta: {
      errorMsgByStatus: RECEIPT_DOWNLOAD_ERROR_MSG_BY_STATUS,
    },
  });
};

// Bill detail reads. `version` omitted = the current version — the behaviour these
// endpoints had before version browsing existed. When supplied it is the
// invoice_sk / cash_sales_sk taken from getTransactionHistory below.
export const getInvoiceDetail = (uuid: string, version?: number) => {
  const url = version
    ? `/invoices/${uuid}?version=${encodeURIComponent(version)}`
    : `/invoices/${uuid}`;
  return axios.get(url);
};

export const getCashSalesDetail = (uuid: string, version?: number) => {
  const url = version
    ? `/cashsales/${uuid}?version=${encodeURIComponent(version)}`
    : `/cashsales/${uuid}`;
  return axios.get(url);
};

// Every stored version of one bill, oldest first. Real payload is at
// response.data.data (TransactionHistoryResponse).
export const getTransactionHistory = (uuid: string) => {
  return axios.get<ApiResponse<TransactionHistoryResponse>>(
    `/transactions/${uuid}/history`,
  );
};

/** The current version of one bill with its lines. */
export const getTransactionWithItems = (uuid: string) =>
  axios.get<ApiResponse<TransactionDetailWithItems>>(
    `/transactions/${encodeURIComponent(uuid)}`,
  );

/**
 * Filters for GET /pricebooks; `search` matches the pricebook code or name. `branch_id` is a
 * branch's company id: only the books that branch's shops may be put on.
 */
export interface PriceBookListFilters {
  search?: string;
  type?: PriceBookType;
  status?: PriceBookStatus;
  created_from?: string;
  created_to?: string;
  branch_id?: string;
  is_draft?: boolean;
  /** Only the books with (true) or without (false) a scheduled draft apply waiting for its day. */
  has_schedule?: boolean;
}

/**
 * The list screen reads the pricebooks newest-first, so one that was just created is the
 * first row instead of the last. Only this screen asks for it: the customer pricebook
 * pickers call the same endpoint without sort params and rely on its `code ASC` default.
 */
const PRICE_BOOK_LIST_SORT_BY = "created";
const PRICE_BOOK_LIST_SORT_ORDER = "desc";

export const getListPriceBook = (
  page: number,
  limit = 10,
  filters: PriceBookListFilters = {},
) => {
  const query = new URLSearchParams();
  query.append("limit", String(limit));
  query.append("page", String(page));
  query.append("sort_by", PRICE_BOOK_LIST_SORT_BY);
  query.append("sort_order", PRICE_BOOK_LIST_SORT_ORDER);
  if (filters.search) query.append("search", filters.search);
  if (filters.type) query.append("type", filters.type);
  if (filters.status) query.append("status", filters.status);
  if (filters.created_from) query.append("created_from", filters.created_from);
  if (filters.created_to) query.append("created_to", filters.created_to);
  if (filters.branch_id) query.append("branch_id", filters.branch_id);
  if (filters.is_draft !== undefined) {
    query.append("is_draft", String(filters.is_draft));
  }
  if (filters.has_schedule !== undefined) {
    query.append("has_schedule", String(filters.has_schedule));
  }
  return axios.get(`/pricebooks?${query.toString()}`);
};

/**
 * How many books have a scheduled draft apply waiting for its day: one row asked for, `count`
 * read. An ambient count for the list banner, so a failure is never shown.
 */
export const countScheduledPricebooks = () => {
  const query = new URLSearchParams({
    page: "1",
    limit: "1",
    has_schedule: String(true),
  });
  return axios.get<ApiResponse<{ count: number }>>(
    `/pricebooks?${query.toString()}`,
    { meta: { suppressErrorToast: true, suppressForbiddenRedirect: true } },
  );
};

export const getPriceBookDetails = (uuid: string) => {
  return axios.get(`/pricebooks/${encodeURIComponent(uuid)}`);
};

/**
 * The `PB###` code the create form prefills, so a pricebook code is never typed. Two
 * people creating at once are handed the same code and `createPriceBook` refuses the
 * second one with a 409, so the caller has to be ready to ask for another.
 */
export const getNextPriceBookCode = () => {
  return axios.get<ApiResponse<PriceBookNextCode>>(`/pricebooks/next-code`);
};

// The drawer and the price save each announce the save once, after every step has landed,
// so none of the requests below toasts on its own.
export const createPriceBook = (data: PriceBookCreateRequest) => {
  return axios.post(`/pricebooks`, data, {
    meta: { suppressForbiddenRedirect: true },
  });
};

export const updatePriceBook = (uuid: string, data: PriceBookUpdateRequest) => {
  return axios.patch(`/pricebooks/${encodeURIComponent(uuid)}`, data, {
    meta: { suppressForbiddenRedirect: true },
  });
};

const DISABLED_PRICE_BOOK_STATUS: PriceBookStatus = "disable";

/** Switches a pricebook off without touching anything else on it — the undoable delete. */
export const deactivatePriceBook = (uuid: string) => {
  return axios.patch(
    `/pricebooks/${encodeURIComponent(uuid)}/status`,
    { status: DISABLED_PRICE_BOOK_STATUS },
    {
      meta: {
        successMsg: "ปิดสมุดราคาสำเร็จ",
        suppressForbiddenRedirect: true,
      },
    },
  );
};

/**
 * Copy a pricebook as a new book. `overrides` carries the values the copy should differ by;
 * anything it omits is taken from the source. The copy's `code` is generated by the API, it
 * opens in 00-HO only, and the source's customers are never copied — a customer holds a
 * single pricebook, so copying them would move those stores off it.
 */
export const duplicatePriceBook = (
  uuid: string,
  overrides?: PriceBookUpdateRequest,
) => {
  return axios.post(
    `/pricebooks/${encodeURIComponent(uuid)}/duplicate`,
    overrides,
    { meta: { suppressForbiddenRedirect: true } },
  );
};

// `mode` of POST /pricebooks/:uuid/duplicate that makes a draft instead of a new live book.
const DRAFT_DUPLICATE_MODE = "draft";

/**
 * Copies the book as a draft: same lines and book %, open in 00-HO only, never mirrored. The
 * source and its branches keep their prices until the draft is applied.
 */
export const createPricebookDraft = (uuid: string) =>
  axios.post<ApiResponse<PriceBookResponse>>(
    `/pricebooks/${encodeURIComponent(uuid)}/duplicate`,
    { mode: DRAFT_DUPLICATE_MODE },
    {
      meta: {
        successMsg: "สร้างร่างสำหรับทดสอบแล้ว",
        suppressForbiddenRedirect: true,
      },
    },
  );

/** What applying the draft would change on its source. The page shows a refusal itself. */
export const getPricebookDiff = (draftUuid: string) =>
  axios.get<ApiResponse<PricebookDraftDiff>>(
    `/pricebooks/${encodeURIComponent(draftUuid)}/diff`,
    { meta: { suppressErrorToast: true } },
  );

/**
 * Applies the draft onto its source now, on a later day (a schedule) or as price periods over a
 * date range; every mode closes the draft and moves its test shops back to the source at once.
 * Answers with the source book. The dialog shows a refusal and announces the success itself,
 * since the wording depends on the mode.
 */
export const applyPricebookDraft = (
  draftUuid: string,
  body: PricebookApplyRequest,
) =>
  axios.post<ApiResponse<PriceBookResponse>>(
    `/pricebooks/${encodeURIComponent(draftUuid)}/apply`,
    body,
    { meta: { suppressErrorToast: true, suppressForbiddenRedirect: true } },
  );

/** The book's scheduled draft applies (ตั้งเวลา tab), newest effective day first; the API pages them. */
export const getPricebookSchedules = (
  uuid: string,
  page: number,
  limit: number,
) => {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  return axios.get<ApiResponse<PricebookSchedulePage>>(
    `/pricebooks/${encodeURIComponent(uuid)}/schedules?${query.toString()}`,
  );
};

/**
 * Cancels a schedule still waiting for its day. The tab shows a refusal (422: it is no longer
 * pending) itself.
 */
export const cancelPricebookSchedule = (scheduleUuid: string) =>
  axios.delete<ApiResponse<PricebookSchedule>>(
    `/pricebook-schedules/${encodeURIComponent(scheduleUuid)}`,
    {
      meta: {
        successMsg: "ยกเลิกการตั้งเวลาแล้ว",
        suppressErrorToast: true,
        suppressForbiddenRedirect: true,
      },
    },
  );

/**
 * Why a draft or schedule request was refused, in the API's own Thai; a 409 means the source moved
 * or is gone, or a range overlaps a period of the source.
 */
export interface PricebookDraftRefusal {
  message: string;
  isConflict: boolean;
}

export const readPricebookDraftRefusal = (
  error: unknown,
): PricebookDraftRefusal | null => {
  if (!isAxiosError<{ message?: string }>(error)) return null;
  const status = error.response?.status;
  const message = error.response?.data?.message;
  const isRefusal =
    status === HTTP_CONFLICT || status === HTTP_UNPROCESSABLE_ENTITY;
  if (!isRefusal || !message) return null;
  return { message, isConflict: status === HTTP_CONFLICT };
};

/** The book's change log, newest first; the API pages it. */
export const getPricebookHistory = (
  uuid: string,
  page: number,
  limit: number,
) => {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  return axios.get<ApiResponse<PricebookHistoryPage>>(
    `/pricebooks/${encodeURIComponent(uuid)}/history?${query.toString()}`,
  );
};

/**
 * The API refuses (409) a pricebook that a store in any branch still uses, and one whose
 * code is reserved by the platform. The refusal is NOT
 * suppressed here: the interceptor shows the backend's reason, the promise still
 * rejects, and `readPriceBookDeleteConflict` reads the counts off the same error
 * so the screen can spell out what is blocking the delete.
 */
export const deletePriceBook = (uuid: string) => {
  return axios.delete(`/pricebooks/${encodeURIComponent(uuid)}`, {
    meta: { successMsg: "ลบสมุดราคาสำเร็จ", suppressForbiddenRedirect: true },
  });
};

/**
 * Detail of a 409 raised by `deletePriceBook`, or null for any other failure. The
 * API puts a refusal's detail in the envelope's `error` slot; `data` is null there.
 */
export const readPriceBookDeleteConflict = (
  error: unknown,
): PriceBookDeleteConflict | null => {
  if (!isAxiosError<{ error?: PriceBookDeleteConflict }>(error)) return null;
  if (error.response?.status !== HTTP_CONFLICT) return null;
  return error.response.data?.error ?? null;
};

/**
 * `search` matches the product's code and name; the API pages the result.
 *
 * Every line is returned whatever its own toggle, which the edit drawer depends on: PUT
 * replaces the whole product set, so a line the drawer never loaded would be dropped on
 * the next save.
 */
export const getPricebookItems = (
  uuid: string,
  page: number,
  limit = 10,
  search?: string,
) => {
  const query = new URLSearchParams();
  query.append("limit", String(limit));
  query.append("page", String(page));
  if (search) query.append("search", search);
  return axios.get(
    `/pricebooks/${encodeURIComponent(uuid)}/items?${query.toString()}`,
  );
};

/** Replaces the whole product set: a product that is not sent leaves the pricebook. */
export const replacePricebookItems = (
  uuid: string,
  items: PricebookItemInput[],
) => {
  return axios.put(
    `/pricebooks/${encodeURIComponent(uuid)}/items`,
    { items },
    { meta: { suppressForbiddenRedirect: true } },
  );
};

/**
 * Prices lines without saving them, next to what is stored now. Refused like the item
 * replace (409 for a reserved pricebook, 422 for an unknown or repeated product).
 */
export const previewPricebookItems = (
  uuid: string,
  body: PricebookItemPreviewRequest,
) =>
  axios.post<ApiResponse<PricebookItemPreviewResponse>>(
    `/pricebooks/${encodeURIComponent(uuid)}/items/preview`,
    body,
    { meta: { suppressForbiddenRedirect: true } },
  );

export const getPricebookBranches = (uuid: string) =>
  axios.get<ApiResponse<PricebookBranches>>(
    `/pricebooks/${encodeURIComponent(uuid)}/branches`,
  );

/** The branch dialog shows every refusal inline, so the global toast is suppressed. */
export const updatePricebookBranches = (
  uuid: string,
  body: PricebookBranchesUpdateRequest,
) =>
  axios.put<ApiResponse<PricebookBranches>>(
    `/pricebooks/${encodeURIComponent(uuid)}/branches`,
    body,
    {
      meta: {
        successMsg: "บันทึกสาขาที่ใช้แล้ว",
        suppressErrorToast: true,
        suppressForbiddenRedirect: true,
      },
    },
  );

/** Why `updatePricebookBranches` was refused: the API's own Thai message, and for a 409 the
 * branches that still have shops on the book. */
export interface PricebookBranchesRefusal {
  message: string;
  branchesWithShops: PricebookBranchWithShops[];
}

export const readPricebookBranchesRefusal = (
  error: unknown,
): PricebookBranchesRefusal | null => {
  if (
    !isAxiosError<{
      message?: string;
      error?: { branches?: PricebookBranchWithShops[] } | null;
    }>(error)
  ) {
    return null;
  }
  const body = error.response?.data;
  if (!body?.message) return null;
  const branchesWithShops =
    error.response?.status === HTTP_CONFLICT ? body.error?.branches ?? [] : [];
  return { message: body.message, branchesWithShops };
};

// ---- ช่วงราคา (price periods of pricebook lines) ----

/** `status` keeps one kind of period; `days` widens or narrows the "ใกล้หมด" window (1–365). */
export interface PricebookPeriodOverviewFilters {
  status?: PricebookPeriodStatus;
  days?: number;
}

/** Every live non-draft book's periods (menu ราคาตามช่วงเวลา); the API pages the result. */
export const getPricebookPeriodOverview = (
  page: number,
  limit: number,
  filters: PricebookPeriodOverviewFilters = {},
) => {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (filters.status) query.append("status", filters.status);
  if (filters.days !== undefined) query.append("days", String(filters.days));
  return axios.get<ApiResponse<PricebookPeriodOverviewPage>>(
    `/pricebooks/periods?${query.toString()}`,
  );
};

const EXPIRING_PERIOD_STATUS: PricebookPeriodStatus = "expiring";

/**
 * How many periods of every book end within the warning window: one row asked for, `count`
 * read. An ambient count for the menu badge and the list banner, so a failure is never shown.
 */
export const countExpiringPricebookPeriods = () => {
  const query = new URLSearchParams({
    page: "1",
    limit: "1",
    status: EXPIRING_PERIOD_STATUS,
  });
  return axios.get<ApiResponse<PricebookPeriodOverviewPage>>(
    `/pricebooks/periods?${query.toString()}`,
    { meta: { suppressErrorToast: true, suppressForbiddenRedirect: true } },
  );
};

export interface PricebookPeriodFilters {
  product_code?: string;
  status?: PricebookPeriodStatus;
}

/** One book's periods, newest end first; the API pages the result. */
export const getPricebookPeriods = (
  uuid: string,
  page: number,
  limit: number,
  filters: PricebookPeriodFilters = {},
) => {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (filters.product_code) query.append("product_code", filters.product_code);
  if (filters.status) query.append("status", filters.status);
  return axios.get<ApiResponse<PricebookPeriodPage>>(
    `/pricebooks/${encodeURIComponent(uuid)}/periods?${query.toString()}`,
  );
};

// The period dialogs show a refusal themselves, so none of the writes below toasts one.
const PERIOD_WRITE_META = {
  suppressErrorToast: true,
  suppressForbiddenRedirect: true,
} as const;

const pricebookPeriodPath = (uuid: string, periodUuid: string) =>
  `/pricebooks/${encodeURIComponent(uuid)}/periods/${encodeURIComponent(periodUuid)}`;

export const createPricebookPeriod = (
  uuid: string,
  body: PricebookPeriodCreateRequest,
) =>
  axios.post<ApiResponse<PricebookPeriod>>(
    `/pricebooks/${encodeURIComponent(uuid)}/periods`,
    body,
    { meta: { ...PERIOD_WRITE_META, successMsg: "เพิ่มช่วงราคาแล้ว" } },
  );

export const updatePricebookPeriod = (
  uuid: string,
  periodUuid: string,
  body: PricebookPeriodUpdateRequest,
) =>
  axios.patch<ApiResponse<PricebookPeriod>>(
    pricebookPeriodPath(uuid, periodUuid),
    body,
    { meta: { ...PERIOD_WRITE_META, successMsg: "บันทึกช่วงราคาแล้ว" } },
  );

/** Moves the period's last day later; its prices stay as they are. */
export const extendPricebookPeriod = (
  uuid: string,
  periodUuid: string,
  endDate: string,
) =>
  axios.post<ApiResponse<PricebookPeriod>>(
    `${pricebookPeriodPath(uuid, periodUuid)}/extend`,
    { end_date: endDate },
    { meta: { ...PERIOD_WRITE_META, successMsg: "ต่อช่วงราคาแล้ว" } },
  );

export const deletePricebookPeriod = (uuid: string, periodUuid: string) =>
  axios.delete<ApiResponse<boolean>>(pricebookPeriodPath(uuid, periodUuid), {
    meta: { ...PERIOD_WRITE_META, successMsg: "ลบช่วงราคาแล้ว" },
  });

/**
 * The API's own Thai reason for refusing a period write (409 overlap or face-price book, 422 a
 * date or price rule), or null for any other failure.
 */
export const readPricebookPeriodRefusal = (error: unknown): string | null => {
  if (!isAxiosError<{ message?: string }>(error)) return null;
  const status = error.response?.status;
  const isRefusal =
    status === HTTP_CONFLICT || status === HTTP_UNPROCESSABLE_ENTITY;
  return isRefusal ? error.response?.data?.message ?? null : null;
};

/**
 * `search` matches the assigned customer's code and name; `branchUuid` narrows the list to
 * one branch's shops (honoured for the roles that read every branch). The API pages the result.
 */
export const getPricebookCustomers = (
  uuid: string,
  page: number,
  limit = 10,
  search?: string,
  branchUuid?: string,
) => {
  const query = new URLSearchParams();
  query.append("limit", String(limit));
  query.append("page", String(page));
  if (search) query.append("search", search);
  if (branchUuid) query.append("branch_uuid", branchUuid);
  return axios.get(
    `/pricebooks/${encodeURIComponent(uuid)}/customers?${query.toString()}`,
  );
};

/**
 * `branchCompanyId` (a shop's `company_id`) keeps only the pricebooks a shop of that branch may
 * be put on: no drafts outside 00-HO, and only the books opened in that branch.
 */
export const getPricebooks = (limit = 100, branchCompanyId?: string | null) => {
  const query = new URLSearchParams({ limit: String(limit) });
  if (branchCompanyId) query.append("branch_id", branchCompanyId);
  return axios.get(`/pricebooks?${query.toString()}`);
};

/**
 * The API's own Thai reason for refusing to put a shop on a pricebook (a draft outside 00-HO,
 * a book not opened in the shop's branch, a disabled or unknown code), or null for any other
 * failure. The customer routes name the refused code under `error.pricebook`.
 */
export const readPricebookBindingRefusal = (error: unknown): string | null => {
  if (
    !isAxiosError<{ message?: string; error?: { pricebook?: unknown } | null }>(
      error,
    ) ||
    error.response?.status !== HTTP_UNPROCESSABLE_ENTITY
  ) {
    return null;
  }
  const body = error.response.data;
  return body?.error?.pricebook !== undefined && body.message
    ? body.message
    : null;
};

export const getProductDetails = (productId: string) => {
  return axios.get(`/products/${productId}`);
};

export const getCustomerDetails = (customerId: string) => {
  return axios.get(`/customers/${customerId}`);
};

// ---- Per-customer print settings (GDP-1357) ----
// Path-based; customerUuid is the customer's `uuid`. The real payload lives at
// response.data.data (CustomerPrintSettings). A GET with no saved config still
// returns 200 with a computed default (is_default: true, uuid: null).
export const getCustomerPrintSettings = (customerUuid: string) => {
  return axios.get(`/customers/${customerUuid}/print-settings`);
};

export const updateCustomerPrintSettings = (
  customerUuid: string,
  body: UpdateCustomerPrintSettingsRequest,
) => {
  return axios.put(`/customers/${customerUuid}/print-settings`, body, {
    meta: { successMsg: "บันทึกการตั้งค่าการพิมพ์แล้ว" },
  });
};

/**
 * `suppressErrorToast` lets the create dialog look at a failure before the user
 * sees it: a route/day with no check-in may still be a สายไม่วิ่ง day with a
 * WEB_IT file to offer. The caller must toast anything it does not handle.
 */
export const createSaleReport = (
  request: SaleRouteRequest,
  options: { suppressErrorToast?: boolean } = {},
) => {
  return axios.post(`/sales-report`, request, {
    meta: {
      successMsg: "สร้างรายงานสำเร็จ",
      suppressErrorToast: options.suppressErrorToast,
    },
  });
};

export const getListSaleReport = (
  page: number,
  limit: number,
  reportDate?: string,
  branchCode?: string,
  billIds?: string,
  customer_name?: string,
  customer_code?: string,
  tax_id?: string,
  routes?: string[],
) => {
  const params = buildSaleReportFilterQuery({
    reportDate,
    branchCode,
    billIds,
    customer_name,
    customer_code,
    tax_id,
    routes,
  });
  params.append("page", String(page));
  params.append("limit", String(limit));

  return axios.get(`/sales-report?${params.toString()}`);
};

export interface SaleReportFilterParams {
  reportDate?: string;
  branchCode?: string;
  billIds?: string;
  customer_name?: string;
  customer_code?: string;
  tax_id?: string;
  routes?: string[];
}

function buildSaleReportFilterQuery(filter: SaleReportFilterParams) {
  const params = new URLSearchParams();

  if (filter.reportDate) params.append("report_date", filter.reportDate);
  if (filter.branchCode && filter.branchCode !== "all") {
    params.append("branch_code", filter.branchCode);
  }
  if (filter.routes && filter.routes.length > 0) {
    filter.routes.forEach((route) => params.append("routes", route));
  }
  if (filter.billIds) params.append("bill_ids", filter.billIds);
  if (filter.customer_name) params.append("customer_name", filter.customer_name);
  if (filter.customer_code) params.append("customer_code", filter.customer_code);
  if (filter.tax_id) params.append("tax_id", filter.tax_id);

  return params;
}

export const exportSaleReportList = (filter: SaleReportFilterParams) => {
  const params = buildSaleReportFilterQuery(filter);
  return axios.get<Blob>(`/sales-report/export?${params.toString()}`, {
    responseType: "blob",
    meta: { suppressForbiddenRedirect: true },
  });
};

export const getListTransactionSaleReport = (
  page: number,
  limit: number,
  routeCode?: string,
  status?: string,
  transactionDate?: Dayjs | null,
  customer_name?: string,
  customer_code?: string,
  tax_id?: string,
) => {
  const params = new URLSearchParams();

  params.append("page", String(page));
  params.append("limit", String(limit));

  if (routeCode && routeCode !== "all")
    params.append("route_code", routeCode.toUpperCase());
  if (status && status !== "all") params.append("transaction_status", status);
  if (transactionDate)
    params.append("transaction_date", transactionDate.format("YYYY-MM-DD"));
  if (customer_name) params.append("customer_name", customer_name);
  if (customer_code) params.append("customer_code", customer_code);
  if (tax_id) params.append("tax_id", tax_id);

  return axios.get(`/sales-report/transactions?${params.toString()}`);
};

export const getSaleReportSummary = (uuid: string, version?: string) => {
  const url = version
    ? `/sales-report/${uuid}/summary?version=${version}`
    : `/sales-report/${uuid}/summary`;
  return axios.get(url);
};

export const getSaleReportItems = (uuid: string, version?: string) => {
  const url = version
    ? `/sales-report/${uuid}/items?version=${version}`
    : `/sales-report/${uuid}/items`;
  return axios.get(url);
};

export const getSaleReportHistory = (uuid: string) => {
  return axios.get(`/sales-report/${uuid}/history`);
};

// SRWP pathway daily-grid (GDP-1272/1273). Read-only matrix of products ×
// bills/pathway-stops. `version` omitted = current version. Real payload lives
// at response.data.data (DailyGridResponse).
export const getSaleReportDailyGrid = (uuid: string, version?: string) => {
  const url = version
    ? `/sales-report/${uuid}/daily-grid?version=${encodeURIComponent(version)}`
    : `/sales-report/${uuid}/daily-grid`;
  return axios.get<ApiResponse<DailyGridResponse>>(url);
};

// The same page as an Excel workbook (built by DocGen from the same figures).
export const exportSaleReportDailyGrid = (uuid: string, version?: string | null) => {
  const url = version
    ? `/sales-report/${uuid}/daily-grid/export?version=${encodeURIComponent(version)}`
    : `/sales-report/${uuid}/daily-grid/export`;
  return axios.get<Blob>(url, {
    responseType: "blob",
    meta: { suppressForbiddenRedirect: true },
  });
};

export const updateSaleTarget = (uuid: string, sales_target: number) => {
  return axios.patch(
    `/sales-report/${uuid}/sales-target`,
    { sales_target },
    {
      meta: { successMsg: "อัพเดทเป้ายอดขายสำเร็จ" },
    },
  );
};

export const submitFinanceSaleReport = (uuid: string) => {
  return axios.post(`/sales-report/${uuid}/submit-finance`, undefined, {
    meta: { successMsg: "ส่งข้อมูลสำเร็จ" },
  });
};

export const approveBranchSaleReport = (uuid: string) => {
  return axios.post(`/sales-report/${uuid}/approve-branch`, undefined, {
    meta: { successMsg: "อนุมัติสำเร็จ" },
  });
};

export const approveHoSaleReport = (uuid: string) => {
  return axios.post(`/sales-report/${uuid}/approve-ho`, undefined, {
    meta: { successMsg: "อนุมัติสำเร็จ" },
  });
};

export const requestChangeHoSaleReport = (uuid: string) => {
  return axios.post(`/sales-report/${uuid}/request-change-ho`, undefined, {
    meta: { successMsg: "ส่งคำขอแก้ไขสำเร็จ" },
  });
};

/**
 * Branch accounting sends a submitted report back to branch finance.
 * `review_note` is required by the API — the finance user has to be told what
 * to fix.
 */
export const requestChangeBranchSaleReport = (
  uuid: string,
  reviewNote: string,
) => {
  return axios.post(
    `/sales-report/${uuid}/request-change-branch`,
    { review_note: reviewNote },
    { meta: { successMsg: "ส่งกลับให้แก้ไขสำเร็จ" } },
  );
};

export const rejectSaleReport = (uuid: string) => {
  return axios.post(`/sales-report/${uuid}/reject`, undefined, {
    meta: { successMsg: "ปฏิเสธรายงานสำเร็จ" },
  });
};

export const exportSaleReportWebIt = (uuid: string) => {
  return axios.get(`/sales-report/${uuid}/export/web-it`, {
    responseType: "blob",
  });
};

// สายไม่วิ่ง: van-load uploaded, no check-in, so no sales report to hang the
// WEB_IT export on. `branch_code` mirrors the sales-report list filter.
export const getNoRunDays = (
  params: NoRunDayParams,
  options: { suppressErrorToast?: boolean } = {},
) => {
  const query = new URLSearchParams();
  query.append("start_date", params.start_date);
  query.append("end_date", params.end_date);
  if (params.route && params.route !== "all") query.append("route", params.route);
  if (params.branch_code && params.branch_code !== "all") {
    query.append("branch_code", params.branch_code);
  }
  if (params.page) query.append("page", String(params.page));
  if (params.limit) query.append("limit", String(params.limit));

  return axios.get<ApiResponse<NoRunDayListResponse>>(
    `/sales-report/no-run-days?${query.toString()}`,
    { meta: { suppressErrorToast: options.suppressErrorToast } },
  );
};

export const exportNoRunDayWebIt = (
  route: string,
  date: string,
  branchCode?: string,
) => {
  const query = new URLSearchParams({ route, date });
  if (branchCode && branchCode !== "all") query.append("branch_code", branchCode);

  return axios.get<Blob>(
    `/sales-report/no-run-days/export/web-it?${query.toString()}`,
    { responseType: "blob" },
  );
};

export const updateMultipleSaleReport = (uuids: string[]) => {
  return axios.patch(
    `/sales-report/multiple-approve`,
    { uuids },
    {
      meta: { successMsg: "อัพเดทรายงานสำเร็จ" },
    },
  );
};

/**
 * Route lists leave out the branch's SPECIAL route (ออเดอร์พิเศษ, `<prefix>99`) unless asked:
 * a shop, a pathway or a van can never be assigned to it. Only the screens that report on
 * what was SOLD — creating a sales report, the dashboard, the bill filters — pass
 * `includeSpecial`, because route 99 carries bills on the days it has special orders.
 */
export interface RouteListOptions {
  includeSpecial?: boolean;
}

export const getAllRoutes = (
  companyId?: string,
  options: RouteListOptions = {},
) => {
  const params = new URLSearchParams();

  if (companyId) params.append("company_id", companyId);
  if (options.includeSpecial) params.append("include_special", "true");
  return axios.get(`/routes?${params.toString()}`);
};

export const getAllRoutesByCompany = (options: RouteListOptions = {}) => {
  const query = options.includeSpecial ? "?include_special=true" : "";
  return axios.get(`/routes/by-company${query}`);
};

/**
 * Every live route of one branch — route 99 included — with its live-store and active-staff
 * counts and the free numbers 01–16. The API pins a branch manager to their own branch and
 * ignores `branchUuid` for them; admin, master and head office must pass one.
 */
export const getRouteManagementList = (branchUuid?: string) => {
  const query = branchUuid
    ? `?branch_uuid=${encodeURIComponent(branchUuid)}`
    : "";
  return axios.get<ApiResponse<RouteManagementList>>(
    `/routes/management${query}`,
  );
};

// Route writes answer an action-level 403 (another branch's route, or test mode for a branch
// manager) that must not replace the page with the Permission Denied screen.
export const createRoute = (data: RouteCreateRequest) => {
  return axios.post<ApiResponse<null>>(`/routes`, data, {
    meta: { successMsg: "สร้างสายส่งสำเร็จ", suppressForbiddenRedirect: true },
  });
};

export const updateRoute = (
  uuid: string,
  data: RouteUpdateRequest,
  successMsg: string,
) => {
  return axios.patch<ApiResponse<boolean>>(
    `/routes/${encodeURIComponent(uuid)}`,
    data,
    { meta: { successMsg, suppressForbiddenRedirect: true } },
  );
};

/**
 * The API refuses (409) a route that live stores or active sales staff still use; the
 * interceptor shows its reason and `readRouteDeleteConflict` reads the counts off the error.
 */
export const deleteRoute = (uuid: string) => {
  return axios.delete<ApiResponse<boolean>>(
    `/routes/${encodeURIComponent(uuid)}`,
    {
      meta: { successMsg: "ลบสายส่งสำเร็จ", suppressForbiddenRedirect: true },
    },
  );
};

/** Counts carried by a 409 from `deleteRoute`, or null for any other failure. */
export const readRouteDeleteConflict = (
  error: unknown,
): RouteUsageCounts | null => {
  if (!isAxiosError<{ error?: RouteUsageCounts }>(error)) return null;
  if (error.response?.status !== HTTP_CONFLICT) return null;
  return error.response.data?.error ?? null;
};

export const getExpressData = (params: ExpressDataParams) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page.toString());
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.transaction_date) {
    queryParams.append("transaction_date", params.transaction_date);
  }
  if (params.branch_code && params.branch_code !== "all") {
    queryParams.append("branch_code", params.branch_code);
  }
  if (params.route_code && params.route_code !== "all") {
    queryParams.append("route_code", params.route_code.toUpperCase());
  }
  if (params.transaction_type) {
    queryParams.append("transaction_type", params.transaction_type);
  }
  if (params.status && params.status.length > 0) {
    params.status.forEach((s) => queryParams.append("status", s));
  }

  return axios.get<ApiResponse<ExpressDataListResponse>>(
    `/express-data?${queryParams.toString()}`,
  );
};

export const exportExpressData = (params: ExpressDataExportParams) => {
  const queryParams = new URLSearchParams();

  queryParams.append("transaction_date", params.transaction_date ?? "");
  if (params.branch_code && params.branch_code !== "all") {
    queryParams.append("branch_code", params.branch_code);
  }
  if (params.route_code && params.route_code !== "all") {
    queryParams.append("route_code", params.route_code.toUpperCase());
  }
  if (params.transaction_type) {
    queryParams.append("transaction_type", params.transaction_type);
  }
  if (params.status && params.status.length > 0) {
    params.status.forEach((s) => queryParams.append("status", s));
  }

  return axios.get(`/express-data/export?${queryParams.toString()}`, {
    responseType: "blob",
  });
};

// ---- ใบลดหนี้ / ใบเพิ่มหนี้ (Express credit / debit note) ----

const CREDIT_DEBIT_NOTES_API = "/credit-debit-notes";

const CREDIT_DEBIT_NOTE_FILTER_KEYS = [
  "note_type",
  "date_from",
  "date_to",
  "status",
  "settlement_method",
  "reason_type",
  "search",
  "branch_code",
] as const;

/** The list's filters as a query string; unset filters are left out. */
const creditDebitNoteFilterQuery = (params: CreditDebitNoteFilterParams) => {
  const search = new URLSearchParams();
  for (const key of CREDIT_DEBIT_NOTE_FILTER_KEYS) {
    const value = params[key];
    if (value) search.append(key, value);
  }
  return search;
};

export const getCreditDebitNotes = (params: CreditDebitNoteListParams) => {
  const search = creditDebitNoteFilterQuery(params);
  search.append("page", String(params.page));
  search.append("limit", String(params.limit));
  return axios.get<ApiResponse<CreditDebitNoteListData>>(
    `${CREDIT_DEBIT_NOTES_API}?${search.toString()}`,
  );
};

/**
 * The list's notes as xlsx (head office only). The interceptor reads the JSON error body back
 * out of the blob, so the server's Thai refusal (range, row limit) reaches the toast.
 */
export const exportCreditDebitNotes = (params: CreditDebitNoteExportParams) =>
  axios.get<Blob>(
    `${CREDIT_DEBIT_NOTES_API}/export?${creditDebitNoteFilterQuery(params).toString()}`,
    { responseType: "blob", meta: { suppressForbiddenRedirect: true } },
  );

/**
 * A live bill's lines with what each can still return, for the note form. The form shows a
 * refusal (cancelled or replaced bill) under the bill search, so the generic toast is suppressed.
 */
export const getCreditDebitNoteOriginalBill = (transactionUuid: string) =>
  axios.get<ApiResponse<CreditDebitNoteOriginalBill>>(
    `${CREDIT_DEBIT_NOTES_API}/original-bills/${encodeURIComponent(transactionUuid)}`,
    { meta: { suppressErrorToast: true, suppressForbiddenRedirect: true } },
  );

export const getCreditDebitNote = (uuid: string) =>
  axios.get<ApiResponse<CreditDebitNoteDetail>>(
    `${CREDIT_DEBIT_NOTES_API}/${encodeURIComponent(uuid)}`,
  );

export const createCreditDebitNote = (body: CreditDebitNoteCreateRequest) =>
  axios.post<ApiResponse<CreditDebitNoteCreateResponse>>(CREDIT_DEBIT_NOTES_API, body, {
    meta: { successMsg: "ออกเอกสารสำเร็จ", suppressForbiddenRedirect: true },
  });

/** The caller shows a refusal inside its own dialog, so the generic toast is suppressed. */
export const cancelCreditDebitNote = (
  uuid: string,
  body: CreditDebitNoteCancelRequest,
) =>
  axios.post<ApiResponse<CreditDebitNoteDetail>>(
    `${CREDIT_DEBIT_NOTES_API}/${encodeURIComponent(uuid)}/cancel`,
    body,
    {
      meta: {
        successMsg: "ยกเลิกเอกสารสำเร็จ",
        suppressErrorToast: true,
        suppressForbiddenRedirect: true,
      },
    },
  );

const CREDIT_DEBIT_NOTE_PDF_ERROR_MSG_BY_STATUS: Record<number, string> = {
  404: "ไม่พบเอกสารนี้",
  502: "สร้างไฟล์ PDF ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
};

/** The note rendered as a PDF stream, fetched as a blob through the authenticated client. */
export const downloadCreditDebitNotePdf = (uuid: string) =>
  axios.get<Blob>(`${CREDIT_DEBIT_NOTES_API}/${encodeURIComponent(uuid)}/pdf`, {
    responseType: "blob",
    meta: {
      errorMsgByStatus: CREDIT_DEBIT_NOTE_PDF_ERROR_MSG_BY_STATUS,
      suppressForbiddenRedirect: true,
    },
  });

/**
 * Build the shared query string for the customer-group exports. `"all"` is the
 * UI's sentinel for "no filter", so it is dropped rather than sent to the API.
 */
const buildCustomerGroupExportQuery = (params: CustomerGroupExportParams) => {
  const queryParams = new URLSearchParams();

  queryParams.append("customer_group_uuid", params.customer_group_uuid);
  queryParams.append("date_from", params.date_from);
  queryParams.append("date_to", params.date_to);
  if (params.branch_code && params.branch_code !== "all") {
    queryParams.append("branch_code", params.branch_code);
  }
  if (params.route && params.route !== "all") {
    queryParams.append("route", params.route.toUpperCase());
  }

  return queryParams.toString();
};

export const exportTescoReport = (params: CustomerGroupExportParams) =>
  axios.get(`/tesco-export/export?${buildCustomerGroupExportQuery(params)}`, {
    responseType: "blob",
  });

export const exportSevenElevenReport = (params: CustomerGroupExportParams) =>
  axios.get(
    `/seven-eleven-export/export?${buildCustomerGroupExportQuery(params)}`,
    { responseType: "blob" },
  );

export const exportTwkReport = (params: CustomerGroupExportParams) =>
  axios.get(`/twk-export/export?${buildCustomerGroupExportQuery(params)}`, {
    responseType: "blob",
  });

// ---- ตั้งค่า export เชน MT (customer_groups.export_* / po_*) ----
// Every role reads the groups (the export page lists its chains from them); only MASTER writes.

export const getCustomerGroupExportSettings = () =>
  axios.get<ApiResponse<CustomerGroupWithExportSettings[]>>("/customer-groups");

export const updateCustomerGroupExportSettings = (
  uuid: string,
  body: CustomerGroupExportSettings,
) =>
  axios.put<ApiResponse<boolean>>(
    `/customer-groups/${encodeURIComponent(uuid)}/export-settings`,
    body,
    { meta: { successMsg: "บันทึกการตั้งค่า export เชนแล้ว" } },
  );

export const exportGb1SaleReport = (params: Gb1SaleReportExportParams) => {
  const queryParams = new URLSearchParams();

  queryParams.append("date", params.date);
  if (params.branch && params.branch !== "all") {
    queryParams.append("branch", params.branch);
  }
  if (params.route && params.route !== "all") {
    queryParams.append("route", params.route);
  }

  return axios.get(`/gb1-sale-report/export?${queryParams.toString()}`, {
    responseType: "blob",
  });
};

export const getInvoiceWaste = (uuid: string, version?: string) => {
  const url = version
    ? `/sales-report/${uuid}/invoice-waste?version=${version}`
    : `/sales-report/${uuid}/invoice-waste`;
  return axios.get<InvoiceWasteResponse>(url);
};

export const getInvoiceWasteSummary = (uuid: string, version?: string) => {
  const url = version
    ? `/sales-report/${uuid}/invoice-waste-summary?version=${version}`
    : `/sales-report/${uuid}/invoice-waste-summary`;
  // The ladder is a secondary panel next to the report it annotates. A report with no
  // waste checker answers 404 here, and a toast for that would fire on every visit to
  // a perfectly readable report — the panel simply does not render instead.
  return axios.get<ApiResponse<InvoiceWasteSummary>>(url, {
    meta: { suppressErrorToast: true },
  });
};

export const updateReconcileDetails = (
  uuid: string,
  request: UpdateReconcileDetailsRequest,
) => {
  return axios.put(`/reconciles/${uuid}/details/`, request);
};

export const getReconcilesHistory = (uuid: string) => {
  return axios.get(`reconciles/${uuid}/history`);
};

export const updateReconcileStatus = (uuid: string, status: string) => {
  return axios.patch(`reconciles/${uuid}/status/`, { status });
};

export const recalculateSaleReport = (uuid: string) => {
  return axios.post(`sales-report/${uuid}/recalculate`);
};

export const getDashboardWidgets = (date: string) => {
  return axios.get(
    `dashboard/widget?date_from=${date}&date_to=${date}&timezone=Asia/Bangkok`,
  );
};

export const getDashboardHoWidgets = (
  dateFrom: string,
  dateTo: string,
  branch_code?: string,
) => {
  const params = new URLSearchParams();
  params.append("date_from", dateFrom);
  params.append("date_to", dateTo);
  params.append("timezone", "Asia/Bangkok");

  if (branch_code && branch_code !== "all") {
    params.append("branch_code", branch_code);
  }

  return axios.get(`dashboard/widget-ho?${params.toString()}`);
};

export const getDashboardSaleSummary = (
  dateFrom: string,
  dateTo?: string,
  branch_code?: string,
) => {
  const params = new URLSearchParams();

  params.append("date_from", dateFrom);
  params.append("date_to", dateTo ?? dateFrom);
  params.append("timezone", "Asia/Bangkok");

  if (branch_code && branch_code !== "all") {
    params.append("branch_code", branch_code);
  }

  return axios.get(`dashboard?${params.toString()}`);
};

export const getDashboardDailyWaste = (date: string) => {
  return axios.get(
    `dashboard/daily-waste?date_from=${date}&date_to=${date}&timezone=Asia/Bangkok`,
  );
};

// ยอดสดรายสายส่งของวันที่เลือก — ไม่รอ reconcile/สร้างรายงาน
export const getDashboardRouteLive = (date: string) => {
  return axios.get<ApiResponse<DashboardRouteLive[]>>(
    `dashboard/route-live?date_from=${date}&date_to=${date}&timezone=Asia/Bangkok`,
  );
};

// สต็อกสดบนรถของสายส่งเดียว รายสินค้า (ใช้ใน modal ที่เปิดจากไอคอนรถ)
export const getRouteLiveStock = (routeUuid: string, date: string) => {
  return axios.get<ApiResponse<RouteLiveStockItem[]>>(
    `/routes/${routeUuid}/live-stock?date=${date}`,
  );
};

export const getDashboardSaleReportSummary = (
  dateFrom: string,
  dateTo: string,
  page: number,
  limit: number,
  status?: string,
  branch_code?: string,
) => {
  const params = new URLSearchParams();

  params.append("date_from", dateFrom);
  params.append("date_to", dateTo);
  params.append("page", String(page));
  params.append("limit", String(limit));
  params.append("timezone", "Asia/Bangkok");

  if (status && status !== "all") {
    params.append("status", status);
  }

  if (branch_code && branch_code !== "all") {
    params.append("branch_code", branch_code);
  }

  return axios.get(`dashboard/sale-report-summary?${params.toString()}`);
};

export const getDashboardSaleReport = (
  dateFrom: string,
  dateTo: string,
  page: number,
  limit: number,
  status?: string,
  branch_code?: string,
) => {
  const params = new URLSearchParams();

  params.append("date_from", dateFrom);
  params.append("date_to", dateTo);
  params.append("page", String(page));
  params.append("limit", String(limit));
  params.append("timezone", "Asia/Bangkok");

  if (status && status !== "all") {
    params.append("status", status);
  }

  if (branch_code && branch_code !== "all") {
    params.append("branch_code", branch_code);
  }

  return axios.get(`dashboard/sale-report?${params.toString()}`);
};

export const getDashboardSalesChart = (
  period: string,
  metric: string,
  dateFrom: string,
  dateTo: string,
  branch_code?: string,
) => {
  const params = new URLSearchParams();

  params.append("period", period);
  params.append("metric", metric);
  params.append("date_from", dateFrom);
  params.append("date_to", dateTo);
  params.append("timezone", "Asia/Bangkok");

  if (branch_code && branch_code !== "all") {
    params.append("branch_code", branch_code);
  }

  return axios.get(`dashboard/daily-sales-breakdown?${params.toString()}`);
};

export const getDashboardWasteBreakdown = (
  dateFrom: string,
  dateTo: string,
  periodType: string,
  branch_code?: string,
) => {
  const params = new URLSearchParams();

  params.append("period_type", periodType);

  if (periodType === "month") {
    params.append("month_from", dateFrom.slice(0, 7));
    params.append("month_to", dateTo.slice(0, 7));
  } else if (periodType === "year") {
    params.append("year_from", dateFrom.slice(0, 4));
    params.append("year_to", dateTo.slice(0, 4));
  } else {
    params.append("date_from", dateFrom);
    params.append("date_to", dateTo);
  }

  params.append("timezone", "Asia/Bangkok");

  if (branch_code && branch_code !== "all") {
    params.append("branch_code", branch_code);
  }

  return axios.get(`dashboard/waste-breakdown?${params.toString()}`);
};

export const getDashboardLeftOverBreakdown = (
  dateFrom: string,
  dateTo: string,
  periodType: string,
  branch_code?: string,
) => {
  const params = new URLSearchParams();

  params.append("period_type", periodType);

  if (periodType === "month") {
    params.append("month_from", dateFrom.slice(0, 7));
    params.append("month_to", dateTo.slice(0, 7));
  } else if (periodType === "year") {
    params.append("year_from", dateFrom.slice(0, 4));
    params.append("year_to", dateTo.slice(0, 4));
  } else {
    params.append("date_from", dateFrom);
    params.append("date_to", dateTo);
  }

  params.append("timezone", "Asia/Bangkok");

  if (branch_code && branch_code !== "all") {
    params.append("branch_code", branch_code);
  }

  return axios.get(`dashboard/leftover-breakdown?${params.toString()}`);
};

export const updateInvoiceComment = (uuid: string, comment: string) => {
  return axios.patch(
    `invoices/${uuid}/comment`,
    { comment },
    {
      meta: { successMsg: "อัพเดทคอมเม้นต์สำเร็จ" },
    },
  );
};

export const updateCashSalesComment = (uuid: string, comment: string) => {
  return axios.patch(
    `cashsales/${uuid}/comment`,
    { comment },
    {
      meta: { successMsg: "อัพเดทคอมเม้นต์สำเร็จ" },
    },
  );
};

export const requestChangeSaleReport = (uuid: string) => {
  return axios.post(`sales-report/${uuid}/request-change`, undefined, {
    meta: { successMsg: "ส่งคำขอแก้ไขสำเร็จ" },
  });
};

export const getListETax = (
  page: number,
  limit = 10,
  dateForm?: Dayjs | null,
  dateTo?: Dayjs | null,
  status?: string,
) => {
  const params = new URLSearchParams();

  params.append("page", page.toString());
  params.append("limit", limit.toString());

  appendDateRange(params, dateForm, dateTo);
  if (status && status !== "ALL") params.append("status", status.toUpperCase());

  return axios.get(`/etax/batches?${params.toString()}`);
};

// The optional range only marks items (`in_range`) and narrows the counted header totals;
// every item of the batch is still returned.
export const getBatchDetails = (
  uuid: string,
  dateFrom?: Dayjs | null,
  dateTo?: Dayjs | null,
) => {
  const params = new URLSearchParams();
  appendDateRange(params, dateFrom, dateTo);
  const search = params.toString();
  return axios.get(`/etax/batches/${uuid}${search ? `?${search}` : ""}`);
};

// No successMsg: the send page shows one result dialog with the counts instead of a toast.
// suppressErrorToast: the page turns a gate refusal (400 + per-bill list) into its own dialog
// and re-raises every other failure through handleAxiosError itself.
export const createETaxBatch = (request: CreateBatchRequest) => {
  return axios.post<ApiResponse<ETaxCreateBatchResult>>(`etax/batches`, request, {
    meta: { suppressErrorToast: true },
  });
};

// Sends or resends notes. Answers like createETaxBatch, so the notes page reads a gate refusal
// (400 + per-note list) the same way and raises every other failure itself.
export const createETaxNoteBatch = (request: ETaxNoteBatchRequest) => {
  return axios.post<ApiResponse<ETaxCreateBatchResult>>(`etax/batches/notes`, request, {
    meta: { suppressErrorToast: true },
  });
};

// Dry run of the same gate createETaxBatch applies, so an operator can find the bills that
// cannot be sent without a failed send. It also records the verdicts, which is what puts the
// "ส่งไม่ได้" badge on the list — nothing checks bills on a schedule.
export const checkETaxEligibility = (
  items: { transaction_uuid: string }[],
) => {
  return axios.post(`etax/batches/check`, { items });
};

// HO's note against a bill's verdict. `note: null` clears it. The backend upsert never
// touches these columns, so a later re-check will not erase what was written here.
export const saveETaxEligibilityNote = (
  transactionUuid: string,
  note: string | null,
) => {
  return axios.patch(
    `etax/batches/eligibility/${transactionUuid}/note`,
    { note },
    { meta: { successMsg: "บันทึกหมายเหตุสำเร็จ" } },
  );
};

export const refreshETax = (uuid: string) => {
  return axios.patch(
    `etax/batches/${uuid}/refresh`,
    { uuid },
    {
      meta: { successMsg: "Refresh E-tax สำเร็จ" },
    },
  );
};

// Same-origin PDF proxy: streams the e-tax item PDF binary so the browser
// downloads it without fetching a cross-origin presigned MinIO/S3 URL (which
// fails CORS). Callers own the error toast, so suppress the generic one.
export const downloadETaxItemPdf = (uuid: string) => {
  return axios.get(`/etax/batches/items/${uuid}/pdf/download`, {
    responseType: "blob",
    // The caller owns the error toast. suppressForbiddenRedirect keeps a 403 as
    // a normal rejection (caller shows its toast) instead of replacing the whole
    // page with the Permission Denied screen mid-download.
    meta: { suppressErrorToast: true, suppressForbiddenRedirect: true },
  });
};

// The digitally-signed tax invoice is not stored on our side — the API fetches it from
// KTB per request (DownloadSignedFileService) and streams it back same-origin, so the
// KTB credentials stay server-side. Callers own the error toast.
export const downloadETaxSignedPdf = (uuid: string) => {
  return axios.get(`/etax/batches/items/${uuid}/signed-pdf/download`, {
    responseType: "blob",
    meta: { suppressErrorToast: true, suppressForbiddenRedirect: true },
  });
};

// Withdraw a document already handed to the filing agent, so the bill goes back to ค้างส่ง
// and can be sent again. The error toast is the interceptor's, deliberately: when KTB
// refuses, the backend passes KTB's own reason through, and that sentence is the whole
// value of the refusal — "the document is already with the Revenue Department" and
// "something went wrong" call for completely different actions from the operator.
export const cancelETaxSubmission = (transactionUuid: string) => {
  return axios.post(
    `etax/batches/documents/${transactionUuid}/cancel`,
    {},
    { meta: { successMsg: "ยกเลิกการนำส่งแล้ว บิลนี้กลับไปอยู่ค้างส่ง" } },
  );
};

// ---- Company profile (/company-profile — every role reads, MASTER edits) ----

export const getCompanyProfile = () => {
  return axios.get<ApiResponse<CompanyProfile>>(`/company-profile`);
};

export const updateCompanyProfile = (request: CompanyProfileUpdateRequest) => {
  return axios.patch<ApiResponse<CompanyProfile>>(`/company-profile`, request, {
    meta: { successMsg: "บันทึกข้อมูลบริษัทสำเร็จ" },
  });
};

export const uploadCompanyLogo = (logo: File) => {
  const formData = new FormData();
  formData.append("logo", logo);
  return axios.post<ApiResponse<CompanyProfile>>(`/company-profile/logo`, formData, {
    meta: { successMsg: "อัปโหลดโลโก้บริษัทสำเร็จ" },
  });
};

// ---- E-Tax weekly auto-send (/etax/auto-send, HO accounting only) ----

// Shown for a manual round started while another round still holds the lock.
export const ETAX_AUTO_SEND_ROUND_RUNNING_MESSAGE = "มีรอบกำลังทำงานอยู่";

// `quiet` is for callers that only borrow a figure from the settings (the retry cap on a
// status chip): a failure there must neither toast nor replace the page with a 403.
export const getETaxAutoSendSettings = (options?: { quiet?: boolean }) => {
  return axios.get(
    `/etax/auto-send/settings`,
    options?.quiet
      ? { meta: { suppressErrorToast: true, suppressForbiddenRedirect: true } }
      : undefined,
  );
};

export const updateETaxAutoSendSettings = (
  request: UpdateEtaxAutoSendSettingsRequest,
) => {
  return axios.put(`/etax/auto-send/settings`, request, {
    meta: { successMsg: "บันทึกการตั้งค่าส่งอัตโนมัติสำเร็จ" },
  });
};

export const getETaxAutoSendRounds = (page: number, limit = 10) => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("limit", limit.toString());
  return axios.get(`/etax/auto-send/rounds?${params.toString()}`);
};

// Plans a manual round without writing anything, so the operator sees what would go first.
export const previewETaxAutoSendRound = () => {
  return axios.post(
    `/etax/auto-send/rounds/run`,
    { dry_run: true },
    {
      meta: {
        errorMsgByStatus: { 409: ETAX_AUTO_SEND_ROUND_RUNNING_MESSAGE },
      },
    },
  );
};

// Starts a manual round. The API answers as soon as the round is RUNNING and keeps
// sending in the background; the rounds list is what reports how it ended.
export const runETaxAutoSendRound = () => {
  return axios.post(
    `/etax/auto-send/rounds/run`,
    { dry_run: false },
    {
      meta: {
        successMsg: "เริ่มรอบส่งแล้ว",
        errorMsgByStatus: { 409: ETAX_AUTO_SEND_ROUND_RUNNING_MESSAGE },
      },
    },
  );
};

export const holdETaxDocument = (
  transactionUuid: string,
  note: string | null,
) => {
  return axios.put(
    `/etax/auto-send/holds/${transactionUuid}`,
    { note },
    { meta: { successMsg: "ตั้งเป็นไม่ส่งอัตโนมัติแล้ว" } },
  );
};

export const releaseETaxDocumentHold = (transactionUuid: string) => {
  return axios.delete(`/etax/auto-send/holds/${transactionUuid}`, {
    meta: { successMsg: "ยกเลิกไม่ส่งอัตโนมัติแล้ว" },
  });
};

/**
 * The auto-send alert counts. With a range, only `held_count` and `expired_count` are scoped to
 * it (same `date_from` / `date_to` as the summary); every other count, `blocked_count`
 * included, stays company-wide. A backend that predates the range ignores the params and
 * returns the global figures.
 */
export const getETaxAutoSendAlerts = (
  filter: Pick<ETaxSummaryFilter, "dateFrom" | "dateTo"> = {},
) => {
  const params = new URLSearchParams();
  appendDateRange(params, filter.dateFrom, filter.dateTo);
  const search = params.toString();
  return axios.get(`/etax/auto-send/alerts${search ? `?${search}` : ""}`);
};

export const acknowledgeETaxItem = (etaxUuid: string, note: string | null) => {
  return axios.post(
    `/etax/auto-send/items/${etaxUuid}/acknowledge`,
    { note },
    { meta: { successMsg: "บันทึกการรับทราบแล้ว" } },
  );
};

// ---- E-Tax dashboard summary + candidate-document detail list ----

// Every E-Tax read shares one date-range serializer so the dashboard cards, the detail
// list, the send page and the send history can never disagree about the active range —
// and so a half-typed picker value is never sent as the literal "Invalid Date" (which
// the API rejects with a 400, leaving the screen looking like "no data").
const appendDateRange = (
  params: URLSearchParams,
  dateFrom?: Dayjs | null,
  dateTo?: Dayjs | null,
): void => {
  const from = formatDateParam(dateFrom);
  const to = formatDateParam(dateTo);
  if (from) params.append("date_from", from);
  if (to) params.append("date_to", to);
};

export const getETaxSummary = (filter: ETaxSummaryFilter = {}) => {
  const params = new URLSearchParams();

  appendDateRange(params, filter.dateFrom, filter.dateTo);
  if (filter.branchCode && filter.branchCode !== "all")
    params.append("branch_code", filter.branchCode);

  return axios.get(`/etax/batches/summary?${params.toString()}`);
};

// The documents list and the notes list take the same filters, so one serializer builds both.
const buildETaxDocumentQuery = (
  page: number,
  limit: number,
  filter: ETaxDocumentFilter,
): string => {
  const params = new URLSearchParams();

  params.append("page", page.toString());
  params.append("limit", limit.toString());

  appendDateRange(params, filter.dateFrom, filter.dateTo);
  if (filter.branchCode && filter.branchCode !== "all")
    params.append("branch_code", filter.branchCode);
  if (filter.documentType && filter.documentType !== "all")
    params.append("document_type", filter.documentType);
  if (filter.sendState) params.append("send_state", filter.sendState);
  if (filter.etaxStatus && filter.etaxStatus !== "all")
    params.append("etax_status", filter.etaxStatus);
  if (filter.paymentType && filter.paymentType !== "all")
    params.append("payment_type", filter.paymentType);
  if (filter.channel && filter.channel !== "all")
    params.append("channel", filter.channel);
  if (filter.routeCode && filter.routeCode !== "all")
    params.append("route_code", filter.routeCode.toUpperCase());
  if (filter.customerName) params.append("customer_name", filter.customerName);
  if (filter.customerCode) params.append("customer_code", filter.customerCode);
  if (filter.taxId?.trim()) params.append("tax_id", filter.taxId.trim());

  return params.toString();
};

export const getETaxDocuments = (
  page: number,
  limit = 10,
  filter: ETaxDocumentFilter = {},
) => {
  return axios.get(
    `/etax/batches/documents?${buildETaxDocumentQuery(page, limit, filter)}`,
  );
};

// ใบลดหนี้ / ใบเพิ่มหนี้ for the E-Tax page. The date range is on the note date, and
// `document_type` is CN or DN; `etaxStatus` takes COMPLETED / IN_PROGRESS / FAILED / NOT_SENT /
// BLOCKED / NEEDS_ACK / EXPIRED (a note has no hold, schedule or retry state).
export const getETaxNotes = (
  page: number,
  limit = 10,
  filter: ETaxDocumentFilter = {},
) => {
  return axios.get<ApiResponse<ETaxNoteListResponse>>(
    `/etax/batches/notes?${buildETaxDocumentQuery(page, limit, filter)}`,
  );
};

// รายงานการหักเงิน — จัดกลุ่มตามสายส่งรายเดือน
// param ส่งเป็น *_uuid เท่านั้น (branch_uuid เฉพาะ HO; branch user ถูก scope จาก JWT)
export const getDeductionList = (
  page: number,
  limit = 10,
  yearMonth?: Dayjs | null,
  branchUuid?: string,
  routeUuid?: string,
) => {
  const params = new URLSearchParams();

  params.append("page", page.toString());
  params.append("limit", limit.toString());

  if (yearMonth) params.append("year_month", yearMonth.format("YYYY-MM"));
  if (branchUuid && branchUuid !== "all")
    params.append("branch_uuid", branchUuid);
  if (routeUuid && routeUuid !== "all") params.append("route_uuid", routeUuid);

  return axios.get(`/deductions-report?${params.toString()}`);
};

// รายละเอียดรายวันของสายส่งในเดือนที่เลือก
export const getDeductionRouteDetail = (
  routeUuid: string,
  yearMonth: Dayjs,
) => {
  const params = new URLSearchParams();

  params.append("year_month", yearMonth.format("YYYY-MM"));

  return axios.get(
    `/deductions-report/route/${routeUuid}?${params.toString()}`,
  );
};

// สรุปยอดหักรวมรายเดือนของพนักงาน 1 คน (ทุกสายที่ออก)
export const getDeductionStaffSummary = (
  staffUuid: string,
  yearMonth: Dayjs,
) => {
  const params = new URLSearchParams();

  params.append("year_month", yearMonth.format("YYYY-MM"));

  return axios.get(
    `/deductions-report/${staffUuid}/staff?${params.toString()}`,
  );
};

export const getEventLogs = (
  page: number,
  limit = 10,
  startDate?: Dayjs | null,
  endDate?: Dayjs | null,
  resourceType?: string,
  action?: string,
) => {
  const params = new URLSearchParams();

  params.append("page", page.toString());
  params.append("limit", limit.toString());

  if (startDate) params.append("start_date", startDate.format("YYYY-MM-DD"));
  if (endDate) params.append("end_date", endDate.format("YYYY-MM-DD"));
  if (resourceType && resourceType !== "all")
    params.append("resource_type", resourceType.toString());
  if (action && action !== "all") params.append("action", action);

  return axios.get(`/event-logs?${params.toString()}`);
};

export const getEventLogsActions = () => {
  return axios.get(`/event-logs/actions`);
};

export const getEventLogsResources = () => {
  return axios.get(`/event-logs/resources`);
};

// Stock Management: upload a branch vanload file (Excel .xlsx/.xlsm or .csv).
// วันที่/สาขา/สมุดราคาถูกอ่านจากในไฟล์ จึงส่งแค่ไฟล์เดียว (max 10MB).
// MIME มาตรฐานตามนามสกุล — บังคับ content-type ของ part ให้ตรง allowlist ของ
// server เสมอ ไม่พึ่ง file.type จากเบราว์เซอร์ (อาจว่าง/ตัวพิมพ์ไม่ตรง → 415)
const VAN_LOAD_MIME_BY_EXT: Record<string, string> = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xlsm: "application/vnd.ms-excel.sheet.macroEnabled.12",
  csv: "text/csv",
};

// ---- Reprint Authorization (สิทธิ์พิมพ์ซ้ำ) — GDP-1304 / GDP-1317 ----
// WEB must always pass an explicit `status` (BE defaults to "active").

export const getListReprintAuth = (params: ReprintAuthListParams) => {
  const query = new URLSearchParams();

  query.append("page", String(params.page));
  query.append("limit", String(params.limit));
  query.append("status", params.status ?? "all");

  if (params.q) query.append("q", params.q);
  if (params.route_code && params.route_code !== "all")
    query.append("route_code", params.route_code);
  if (params.created_by && params.created_by !== "all")
    query.append("created_by", params.created_by);
  if (params.date_from) query.append("date_from", params.date_from);
  if (params.date_to) query.append("date_to", params.date_to);

  return axios.get(`/reprint-authorizations?${query.toString()}`);
};

export const createReprintAuth = (body: CreateReprintAuthRequest) => {
  return axios.post(`/reprint-authorizations`, removeEmpty(body), {
    meta: {
      successMsg: "สร้างสิทธิ์พิมพ์ซ้ำสำเร็จ",
      // A bill can carry only one grant that still has quota left; the backend 409s
      // otherwise and names the blocking grant in `error`. CreateGrantModal renders that
      // inline — what is still unprinted on it, and a one-tap revoke — so a global toast
      // here would only say less, twice.
      suppressErrorToast: true,
    },
  });
};

// BE does not persist a revoke reason, so no body is sent.
export const revokeReprintAuth = (uuid: string) => {
  return axios.patch(`/reprint-authorizations/${uuid}/revoke`, undefined, {
    meta: { successMsg: "ยกเลิกสิทธิ์พิมพ์ซ้ำแล้ว" },
  });
};

export const getReprintAuthById = (uuid: string) => {
  return axios.get(`/reprint-authorizations/${uuid}`);
};

export const getBillPrintHistory = (
  transactionUuid: string,
  page = 1,
  limit = 100,
) => {
  const query = new URLSearchParams();

  query.append("transaction_uuid", transactionUuid);
  query.append("page", String(page));
  query.append("limit", String(limit));

  return axios.get(`/bill-prints?${query.toString()}`);
};

// ---- Reprint Requests (คำขอพิมพ์ซ้ำ) — GDP-1398 / GDP-1401 ----
// Salesperson-initiated requests reviewed by approvers. The BE auto-scopes the
// list from the JWT: branch approvers see their own branch, HO/admin/master see
// all branches. Approving spawns a reprint_authorization grant (validity fixed
// at 3h server-side — the web sends NO duration).

export const getReprintRequests = (params: ReprintRequestListParams) => {
  const query = new URLSearchParams();

  query.append("page", String(params.page));
  query.append("limit", String(params.limit));

  // `status` is a strict ENUM on the BE (PENDING/APPROVED/REJECTED) with NO "all"
  // member — omitting it returns every status. Sending "all" would 400.
  if (params.status && params.status !== "all") {
    query.append("status", params.status);
  }
  if (params.requested_by) query.append("requested_by", params.requested_by);
  if (params.transaction_uuid)
    query.append("transaction_uuid", params.transaction_uuid);

  return axios.get(`/reprint-requests?${query.toString()}`);
};

// Approval takes no body — the grant's validity is fixed at 3h by the BE.
export const approveReprintRequest = (uuid: string) => {
  return axios.patch(`/reprint-requests/${uuid}/approve`, undefined, {
    meta: {
      successMsg: "อนุมัติคำขอพิมพ์ซ้ำแล้ว",
      errorMsgByStatus: {
        404: "ไม่พบคำขอพิมพ์ซ้ำนี้ อาจถูกดำเนินการไปแล้ว",
        // Two distinct BE conflicts land on 409: the request is no longer
        // PENDING, or the bill already has a grant WITH QUOTA LEFT (raised by the
        // reused createGrant flow — a spent grant no longer blocks). Cover both
        // without guessing which.
        409: "ไม่สามารถอนุมัติได้ คำขอนี้ถูกดำเนินการไปแล้ว หรือบิลนี้มีสิทธิ์พิมพ์ซ้ำที่ยังใช้ไม่หมด",
      },
    },
  });
};

// `review_note` is optional (BE max 255). Omit the key entirely when blank.
export const rejectReprintRequest = (uuid: string, review_note?: string | null) => {
  const body = review_note ? { review_note } : {};

  return axios.patch(`/reprint-requests/${uuid}/reject`, body, {
    meta: {
      successMsg: "ปฏิเสธคำขอพิมพ์ซ้ำแล้ว",
      errorMsgByStatus: {
        404: "ไม่พบคำขอพิมพ์ซ้ำนี้ อาจถูกดำเนินการไปแล้ว",
        409: "ไม่สามารถปฏิเสธได้ คำขอนี้ถูกดำเนินการไปแล้ว",
      },
    },
  });
};

// ---- Sale Deposit report (รายงานการโอน) — HO + BO consume the same records ----
// Branch pinning is via the token; HO/MASTER may pass branch_code. status
// skips "all"; date range is optional. Slip URLs are presigned by the backend.
/**
 * The filter in the wire's own words, shared by the route-level list and the branch-day
 * summary so the two altitudes of the same report cannot narrow their rows differently.
 */
const UNFILTERED_OPTION_VALUE = "all";

const saleDepositFilterFields = (
  filter: SaleDepositReportFilter,
): Record<string, string> => {
  const fields: Record<string, string> = {};

  if (filter.dateFrom) fields.date_from = filter.dateFrom;
  if (filter.dateTo) fields.date_to = filter.dateTo;
  if (filter.branchCode && filter.branchCode !== UNFILTERED_OPTION_VALUE)
    fields.branch_code = filter.branchCode;
  if (filter.routeCode && filter.routeCode !== UNFILTERED_OPTION_VALUE)
    fields.route_code = filter.routeCode;
  if (filter.status && filter.status !== UNFILTERED_OPTION_VALUE)
    fields.status = filter.status;

  return fields;
};

export const getSaleDepositReport = (
  page: number,
  limit: number,
  filter: SaleDepositReportFilter = {},
) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    ...saleDepositFilterFields(filter),
  });

  return axios.get<ApiResponse<PaginatedSaleDeposit>>(
    `/sale-deposits?${params.toString()}`,
  );
};

/**
 * รายงานการโอน at branch-day altitude: one row per (สาขา, วัน) instead of one per สายส่ง.
 *
 * Head office reviews a branch at a time, so twenty branches on one day are twenty rows here
 * against sixty-odd on the route-level list. The row is a branch-DAY because ปิดศูนย์เซล is
 * decided per branch-day — a row spanning two days could carry no closing status.
 *
 * `status` narrows the deposits being aggregated (the list's own review filter);
 * `closing_status` narrows the branch-days by where their closing stands, and "open" is the
 * absence of a closing row, so only the server can answer it.
 */
export const getSaleDepositBranchSummary = (
  page: number,
  limit: number,
  filter: SaleDepositBranchSummaryFilter,
) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    ...saleDepositFilterFields(filter),
  });

  if (filter.closingStatus && filter.closingStatus !== UNFILTERED_OPTION_VALUE) {
    params.set("closing_status", filter.closingStatus);
  }

  return axios.get<ApiResponse<PaginatedBranchDaySummary>>(
    `/sale-deposits/branch-summary?${params.toString()}`,
  );
};

// The money snapshot แจ้งยอดโอน works from, plus any row already saved for this check-in.
// Read this rather than reusing the dashboard row: the dashboard's cash figure is EX-VAT
// (reporting basis) while remittance is computed VAT-inclusive, so the two differ by ~7%.
export const getSaleDepositCurrent = (checkinUuid: string) => {
  return axios.get<ApiResponse<SaleDepositCurrentResponse>>(
    `/sale-deposits/current?checkin_uuid=${encodeURIComponent(checkinUuid)}`,
  );
};

export const createSaleDepositTransferNotification = (
  request: CreateTransferNotificationRequest,
) => {
  return axios.post<ApiResponse<SaleDepositResponse>>(
    "/sale-deposits/transfer-notification",
    request,
    {
      meta: { successMsg: "บันทึกยอดที่ต้องโอนสำเร็จ" },
    },
  );
};

// Correct ONE slip's amount when the salesperson keyed in a figure that does not match the
// slip image. Only the amount is correctable — the image, date and time are not. The server
// re-derives the deposit's total from its slips and preserves the salesperson's original
// figure, so the correction stays auditable. Refused once the deposit is ตรวจแล้ว.
export const updateSaleDepositSlipAmount = (
  depositUuid: string,
  slipUuid: string,
  transferAmount: number,
) => {
  return axios.patch<ApiResponse<SaleDepositResponse>>(
    `/sale-deposits/${depositUuid}/slips/${slipUuid}`,
    { transfer_amount: transferAmount },
    {
      meta: {
        successMsg: "แก้ไขยอดโอนสำเร็จ",
        errorMsgByStatus: {
          409: "ไม่สามารถแก้ไขได้ เนื่องจากรายการนี้ถูกตรวจสอบแล้ว",
        },
      },
    },
  );
};

/**
 * Correct the expense figures on a deposit the salesperson has already submitted slips for.
 *
 * PATCH semantics: an omitted key leaves the stored figure alone. `additional_expenses` is
 * the exception — sending it replaces the whole array, so the caller must send the complete
 * list it wants to end up with, never just the row it changed. The server re-derives
 * `total_expense_amount` and `required_transfer_amount`, so the response is what the row
 * must be redrawn from. Refused once head office has signed the deposit off.
 */
export const updateSaleDepositExpenses = (
  depositUuid: string,
  expenses: UpdateSaleDepositExpensesRequest,
) => {
  return axios.patch<ApiResponse<SaleDepositResponse>>(
    `/sale-deposits/${depositUuid}/expenses`,
    expenses,
    {
      meta: {
        successMsg: "แก้ไขค่าใช้จ่ายสำเร็จ",
        errorMsgByStatus: {
          409: "ไม่สามารถแก้ไขได้ เนื่องจากรายการนี้ถูกตรวจสอบโดยสำนักงานใหญ่แล้ว",
        },
      },
    },
  );
};

// Toggle the caller's own ตรวจแล้ว flag. WHICH of the two checkboxes this writes is decided
// by the server from the token's roles, never sent from here.
export const updateSaleDepositReview = (uuid: string, reviewed: boolean) => {
  return axios.patch(
    `/sale-deposits/${uuid}/review`,
    { reviewed },
    {
      meta: {
        successMsg: "อัพเดทสถานะการตรวจสอบสำเร็จ",
        errorMsgByStatus: {
          409: "ไม่สามารถอัพเดทสถานะได้ กรุณารีเฟรชหน้าจอแล้วลองใหม่",
        },
      },
    },
  );
};

/**
 * Set the caller's own ตรวจแล้ว flag on the rows the user has SELECTED, in one request.
 *
 * The uuids are named explicitly rather than resolved from a filter: the reviewer ticks a
 * selection they can see on screen, and a filter would reach past the page they were
 * looking at. WHICH of the two checkboxes this writes still comes from the token's roles,
 * never from here.
 *
 * Rows the caller may not tick are skipped and counted rather than failing the call, so there
 * is no `successMsg` here: the counts are the server's to report, and the caller pushes the
 * notification once it has them — "สำเร็จ" over 58 updated and 6 skipped would be a lie.
 */
export const updateSaleDepositReviews = (
  uuids: string[],
  reviewed: boolean,
) => {
  return axios.patch<ApiResponse<SaleDepositBulkReviewResult>>(
    "/sale-deposits/reviews",
    { uuids, reviewed },
  );
};

/**
 * BO attaching the transfer slips a salesperson never sent.
 *
 * Same multipart contract as the field submission, addressed by the deposit's own uuid — BO
 * has no check-in to anchor on. Slips are append-only: this adds to the set, it never
 * replaces it. Refused once head office has signed the deposit off.
 */
export const addSaleDepositSlips = (
  depositUuid: string,
  slips: { file: File; metadata: SaleDepositSlipMetadata }[],
  note?: string | null,
) => {
  const formData = new FormData();
  slips.forEach(({ file }) => formData.append("slips", file, file.name));
  formData.append(
    "slip_metadata",
    JSON.stringify(slips.map(({ metadata }) => metadata)),
  );
  if (note != null) formData.append("note", note);

  return axios.post<ApiResponse<SaleDepositResponse>>(
    `/sale-deposits/${depositUuid}/slips`,
    formData,
    {
      meta: {
        successMsg: "แนบสลิปสำเร็จ",
        errorMsgByStatus: {
          409: "ไม่สามารถแนบสลิปได้ เนื่องจากรายการนี้ถูกตรวจสอบโดยสำนักงานใหญ่แล้ว",
        },
      },
    },
  );
};

// Add / edit the single หมายเหตุ value (shared by PWA-submitted + web-edited).
export const updateSaleDepositNote = (uuid: string, note: string | null) => {
  return axios.patch(
    `/sale-deposits/${uuid}/note`,
    { note },
    {
      meta: { successMsg: "บันทึกหมายเหตุสำเร็จ" },
    },
  );
};

/**
 * Everything the ปิดศูนย์เซล panel shows for ONE branch-day, in one request: the two ตรวจแล้ว
 * sign-offs, the money the closing is checked against, and whether it may be closed at all.
 *
 * Read by the whole review cohort — the branch back office and head-office accounting see the
 * panel exactly as the supervisor does; only the two writes below are the supervisor's alone.
 */
export const getSaleCenterClosingSummary = (
  branchCode: string,
  businessDate: string,
) => {
  const params = new URLSearchParams({
    branch_code: branchCode,
    business_date: businessDate,
  });

  return axios.get<ApiResponse<SaleCenterClosingSummary>>(
    `/sale-center-closings/summary?${params.toString()}`,
  );
};

/**
 * Close a branch-day: the supervisor keys in the cash head office actually received against
 * Express, and the day is frozen — no further corrections from the branch.
 *
 * The response is the same summary the panel already renders, so the caller redraws from it
 * rather than refetching. A 409 means the branch-day moved under the panel (closed by someone
 * else, or a precondition that held when the summary was read no longer does).
 */
export const closeSaleCenter = (request: CloseSaleCenterRequest) => {
  return axios.post<ApiResponse<SaleCenterClosingSummary>>(
    "/sale-center-closings",
    request,
    {
      meta: {
        successMsg: "ปิดศูนย์เซลสำเร็จ",
        errorMsgByStatus: {
          409: "ไม่สามารถปิดศูนย์เซลได้ ข้อมูลของสาขานี้เปลี่ยนไปแล้ว กรุณารีเฟรชหน้าจอแล้วลองใหม่",
        },
      },
    },
  );
};

/**
 * Hand a branch-day back to the branch. Clears every ตรวจแล้ว tick in it and reopens the day
 * for editing, which is why the reason is required: it is the only record of why the branch
 * lost sign-offs it had already given.
 */
export const sendBackSaleCenter = (request: SendBackSaleCenterRequest) => {
  return axios.post<ApiResponse<SaleCenterClosingSummary>>(
    "/sale-center-closings/send-back",
    request,
    {
      meta: { successMsg: "ส่งกลับให้สาขาแก้ไขสำเร็จ" },
    },
  );
};

/**
 * คำขอแก้ไขยอดโอน — the branch asking for ONE closed deposit to be reopened.
 *
 * Raised only while the deposit's branch-day is closed: while it is open the branch simply
 * edits, and a request would be noise. `reason` is required by the server and is the only
 * thing the supervisor has to decide on, so it is never sent blank from here.
 */
export const createSaleDepositEditRequest = (
  request: CreateSaleDepositEditRequestRequest,
) => {
  return axios.post<ApiResponse<SaleDepositEditRequest>>(
    "/sale-deposit-edit-requests",
    request,
    {
      meta: {
        successMsg: "ส่งคำขอแก้ไขยอดโอนแล้ว",
        errorMsgByStatus: {
          409: "ไม่สามารถส่งคำขอได้ รายการนี้มีคำขอที่รออนุมัติอยู่แล้ว หรือพ้นกำหนดขอแก้ไข",
        },
      },
    },
  );
};

/**
 * The queue itself. Read by the whole review cohort — the branch watches where its own
 * request stands, head office works the pending list — and the server's tenant scope decides
 * which branches' requests come back, so no branch is pinned from here for a branch user.
 *
 * The date range narrows the DEPOSIT's business date (วันที่ทำรายการ), not the day the
 * request was raised. Ordering is the server's: pending first, then newest request.
 */
export const getSaleDepositEditRequests = (
  page: number,
  limit: number,
  filter: SaleDepositEditRequestFilter = {},
) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (filter.status && filter.status !== UNFILTERED_OPTION_VALUE)
    params.set("status", filter.status);
  if (filter.dateFrom) params.set("date_from", filter.dateFrom);
  if (filter.dateTo) params.set("date_to", filter.dateTo);
  if (filter.branchCode && filter.branchCode !== UNFILTERED_OPTION_VALUE)
    params.set("branch_code", filter.branchCode);
  if (filter.routeCode && filter.routeCode !== UNFILTERED_OPTION_VALUE)
    params.set("route_code", filter.routeCode);

  return axios.get<ApiResponse<PaginatedSaleDepositEditRequest>>(
    `/sale-deposit-edit-requests?${params.toString()}`,
  );
};

/**
 * How many requests are waiting, for the badge on the คำขอแก้ไขยอดโอน tab — counted
 * server-side under the caller's own tenant scope, so it never depends on which page anyone
 * is looking at.
 */
export const getSaleDepositEditRequestPendingCount = () => {
  return axios.get<ApiResponse<SaleDepositEditRequestPendingCount>>(
    "/sale-deposit-edit-requests/pending-count",
  );
};

/**
 * The supervisor's decision. Approving reopens the branch-day and clears the ตรวจแล้ว ticks
 * on THIS deposit alone, so the day has to be closed again once the correction is checked —
 * which is why the screen confirms before calling this.
 */
export const reviewSaleDepositEditRequest = (
  uuid: string,
  review: ReviewSaleDepositEditRequestRequest,
) => {
  return axios.patch<ApiResponse<SaleDepositEditRequest>>(
    `/sale-deposit-edit-requests/${uuid}`,
    review,
    {
      meta: {
        successMsg:
          review.status === "approved"
            ? "อนุมัติคำขอแก้ไขยอดโอนแล้ว"
            : "ปฏิเสธคำขอแก้ไขยอดโอนแล้ว",
        errorMsgByStatus: {
          409: "ไม่สามารถดำเนินการได้ คำขอนี้ถูกพิจารณาไปแล้ว กรุณารีเฟรชหน้าจอ",
        },
      },
    },
  );
};

export const uploadVanLoad = (file: File) => {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const mimeType = VAN_LOAD_MIME_BY_EXT[ext] || file.type;
  // wrap เป็น Blob ใหม่เพื่อกำหนด content-type ของ part อย่างชัดเจน
  const normalizedFile = new Blob([file], { type: mimeType });

  const formData = new FormData();
  // ต้องส่ง file.name เป็น arg ที่ 3 เสมอ: server ไม่ได้ตรวจ "ชื่อไฟล์" แต่อ่าน
  // "นามสกุล" จาก filename ของ part — Blob ไม่มีชื่อในตัว ถ้าไม่ส่งจะกลายเป็น
  // "blob" (ไม่มีนามสกุล) → extension check fail → 415
  formData.append("file", normalizedFile, file.name);

  return axios.post<VanLoadUploadResponse>(`/van-load/upload`, formData, {
    meta: {
      // 403 ที่นี่คือ "อัปข้ามสาขาไม่ได้" (action-level) ไม่ใช่ห้ามเข้าหน้า
      // จึงไม่ให้เด้งหน้า Permission Denied แต่แสดงเป็น toast แทน
      suppressForbiddenRedirect: true,
      // ข้อความไทยสำหรับ status ที่ backend มักตอบเป็นข้อความเทคนิค/อังกฤษ
      // จงใจไม่ override 422 เพื่อคงรายละเอียด validation จาก backend
      // (สินค้า/แถว/รถที่ผิด) ซึ่งมีประโยชน์กว่าข้อความ generic
      errorMsgByStatus: {
        400: "ไม่พบไฟล์ หรือไฟล์มีขนาดใหญ่เกินไป (สูงสุด 10MB)",
        403: "คุณไม่มีสิทธิ์อัปโหลดข้อมูลของสาขานี้",
        409: "อัปโหลดไม่ได้: มีรอบกระทบยอดที่ถูกอนุมัติแล้ว (ถูกล็อก)",
        415: "ไฟล์ไม่รองรับ กรุณาอัปโหลดไฟล์ .xlsx, .xlsm หรือ .csv เท่านั้น",
      },
    },
  });
};

// ---- Pathway upload (แผนวิ่งสาขา) — preview แล้วค่อย confirm ----
// preview ไม่เขียน DB เลย: ระบบเก็บแผนไว้ชั่วคราว 30 นาที แล้ว confirm ถึงเขียนจริง
// จากแผนที่เก็บไว้ (ไม่ได้อ่านไฟล์ซ้ำ) — สิ่งที่เขียนคือสิ่งที่ผู้ใช้เห็นใน preview

// MIME มาตรฐานตามนามสกุล — ใช้ร่วมกันโดยทุก flow ที่อัปไฟล์ Excel (.xlsx/.xls)
// ทั้งแผนวิ่งและ PO ไม่พึ่ง file.type จากเบราว์เซอร์ (อาจว่าง/ตัวพิมพ์ไม่ตรง → 415)
const SPREADSHEET_MIME_BY_EXT: Record<string, string> = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
};

export const previewPathwayUpload = (file: File, branchUuid?: string) => {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const mimeType = SPREADSHEET_MIME_BY_EXT[ext] || file.type;
  // wrap เป็น Blob ใหม่เพื่อกำหนด content-type ของ part อย่างชัดเจน
  const normalizedFile = new Blob([file], { type: mimeType });

  const formData = new FormData();
  // ส่ง file.name เป็น arg ที่ 3 เสมอ: server อ่าน "นามสกุล" จาก filename ของ part
  // ถ้าไม่ส่ง Blob จะกลายเป็น "blob" (ไม่มีนามสกุล) → 415
  formData.append("file", normalizedFile, file.name);
  if (branchUuid) formData.append("branch_uuid", branchUuid);

  return axios.post<PathwayUploadPreviewResponse>(
    `/pathway/upload/preview`,
    formData,
    {
      meta: {
        // 403 ที่นี่คือ "อัปข้ามสาขาไม่ได้" (action-level) ไม่ใช่ห้ามเข้าหน้า
        suppressForbiddenRedirect: true,
        // ไม่ใช้ global toast: เหตุผลที่ไฟล์ไม่ผ่านต้องแสดงคู่กับปุ่มดาวน์โหลด
        // แบบฟอร์ม ตรงจุดที่ผู้ใช้เพิ่งเลือกไฟล์ — resolvePreviewFailure() ใน
        // components/PathwayUpload/uploadError.ts เป็นคนแปลงข้อความ
        suppressErrorToast: true,
      },
    },
  );
};

/**
 * แบบฟอร์มแผนวิ่งเปล่า (.xlsx) — backend สร้างสดจาก layout เดียวกับ parser
 * จึงไม่มีทางหลุดจากรูปแบบที่ระบบอ่านได้
 */
export const downloadPathwayUploadTemplate = (branchUuid?: string) => {
  return axios.get<Blob>(`/pathway/upload/template`, {
    // HO ส่งสาขาที่เลือกมา จำนวนชีต ผ. จะเท่ากับจำนวนสายของสาขานั้น
    params: branchUuid ? { branch_uuid: branchUuid } : undefined,
    responseType: "blob",
    meta: {
      suppressForbiddenRedirect: true,
      errorMsgByStatus: {
        403: "คุณไม่มีสิทธิ์ดาวน์โหลดแบบฟอร์มแผนวิ่ง",
      },
    },
  });
};

export const confirmPathwayUpload = (
  uploadId: string,
  acknowledgeSkipped: boolean,
) => {
  return axios.post<PathwayUploadConfirmResponse>(
    `/pathway/upload/${uploadId}/confirm`,
    { acknowledge_skipped: acknowledgeSkipped },
    {
      meta: {
        suppressForbiddenRedirect: true,
        errorMsgByStatus: {
          403: "รายการตรวจสอบนี้เป็นของผู้ใช้หรือสาขาอื่น",
          404: "รายการตรวจสอบหมดอายุหรือถูกใช้ไปแล้ว กรุณาอัปโหลดไฟล์ใหม่",
        },
      },
    },
  );
};

// ---- PO โมเดิร์นเทรด (เฟส A) — /purchase-orders ----
// นำเข้าไฟล์ PO เป็น 2 ขั้นเหมือนแผนวิ่ง: preview ไม่เขียน DB (พักผลตรวจไว้ 30 นาที)
// แล้ว confirm ถึงเขียนจริงจากผลที่พักไว้ ไม่ได้อ่านไฟล์ซ้ำ
//
// ข้อความปฏิเสธของ endpoint ชุดนี้เป็นภาษาไทยและบอกสาเหตุจริงมาจาก backend อยู่แล้ว
// (403 แตะ PO ของสาขาอื่น, 422 ร้านผิดเชน, 422 PO ถูกใช้แล้ว, 422 ยังเลือกสาขาไม่ครบ)
// จึงจงใจไม่ override ด้วย errorMsgByStatus — ข้อความ generic จะบอกน้อยกว่าเดิม

export const previewPurchaseOrderImport = (
  file: File,
  chain: PurchaseOrderChain,
) => {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const mimeType = SPREADSHEET_MIME_BY_EXT[ext] || file.type;
  // wrap เป็น Blob ใหม่เพื่อกำหนด content-type ของ part อย่างชัดเจน
  const normalizedFile = new Blob([file], { type: mimeType });

  const formData = new FormData();
  // ส่ง file.name เป็น arg ที่ 3 เสมอ: server อ่าน "นามสกุล" จาก filename ของ part
  // ถ้าไม่ส่ง Blob จะกลายเป็น "blob" (ไม่มีนามสกุล) → 415
  formData.append("file", normalizedFile, file.name);
  formData.append("chain", chain);

  return axios.post<ApiResponse<PurchaseOrderImportPreviewData>>(
    `/purchase-orders/import/preview`,
    formData,
    {
      meta: {
        // 403 ที่นี่คือ "ไม่ใช่ HO" (action-level) ไม่ใช่ห้ามเข้าหน้า
        suppressForbiddenRedirect: true,
        // ไม่ใช้ global toast: เหตุผลที่ไฟล์ไม่ผ่านต้องแสดงคู่กับปุ่มเลือกไฟล์
        // ตรงจุดที่ผู้ใช้เพิ่งเลือกไฟล์ — จอ W2 เป็นคนแปลงและแสดงข้อความนั้นเอง
        suppressErrorToast: true,
      },
    },
  );
};

export const confirmPurchaseOrderImport = (
  uploadId: string,
  branchChoices: PurchaseOrderImportBranchChoice[],
  includePastRowNos: number[] = [],
) => {
  // ไม่มีแถวที่เลือกสาขาไว้ = ไม่ส่งคีย์นี้เลย: backend รับ branch_choices เป็น
  // optional และคาดหวัง undefined ไม่ใช่ null หรืออาเรย์ว่าง · แถว ต้องเลือกสาขา ที่ยัง
  // ไม่เลือกไม่ถูกปฏิเสธแล้ว แต่ถูกบันทึกเป็น ยังไม่ระบุสาขา (no_branch_count)
  // แถวเกินกำหนดส่งที่ไม่อยู่ใน include_past_row_nos ถูกข้าม · ไม่มีก็ไม่ส่งคีย์
  const body = {
    ...(branchChoices.length > 0 ? { branch_choices: branchChoices } : {}),
    ...(includePastRowNos.length > 0
      ? { include_past_row_nos: includePastRowNos }
      : {}),
  };

  return axios.post<ApiResponse<PurchaseOrderImportConfirmData>>(
    `/purchase-orders/import/${uploadId}/confirm`,
    body,
    {
      // 403 = ผลตรวจของผู้ใช้หรือสาขาอื่น ซึ่งเป็นผลของปุ่มยืนยัน ไม่ใช่ห้ามเข้าหน้า
      meta: { suppressForbiddenRedirect: true },
    },
  );
};

// ใช้ร่วมกันระหว่างรายการกับ Export ไฟล์ที่ดาวน์โหลดจึงเป็นชุดเดียวกับที่ตารางแบ่งหน้าอยู่
const buildPurchaseOrderFilterQuery = (filter: PurchaseOrderListFilter) => {
  const query = new URLSearchParams();

  if (filter.delivery_from) query.append("delivery_from", filter.delivery_from);
  if (filter.delivery_to) query.append("delivery_to", filter.delivery_to);
  if (filter.company_id) query.append("company_id", filter.company_id);
  if (filter.chain) query.append("chain", filter.chain);
  if (filter.status) query.append("status", filter.status);
  if (filter.q) query.append("q", filter.q);
  if (filter.batch_uuid) query.append("batch_uuid", filter.batch_uuid);
  if (filter.route_number) query.append("route_number", filter.route_number);

  return query;
};

// 403 ที่นี่คือ role ที่ไม่มีจอ PO เลย จึงปล่อยให้ interceptor พาไป Permission Denied
// ตามปกติ (page-level) ต่างจาก action อื่นในกลุ่มนี้ที่เปิดคร่อมรายการอยู่
export const listPurchaseOrders = (params: PurchaseOrderListParams) => {
  const query = buildPurchaseOrderFilterQuery(params);
  query.append("page", String(params.page));
  query.append("limit", String(params.limit));

  return axios.get<ApiResponse<PurchaseOrderListData>>(
    `/purchase-orders?${query.toString()}`,
  );
};

export const getPurchaseOrder = (uuid: string) => {
  return axios.get<ApiResponse<PurchaseOrderDetail>>(
    `/purchase-orders/${uuid}`,
    {
      // W6 เป็น drawer ที่เปิดคร่อมรายการ · 403 ที่นี่คือ "PO ของสาขาอื่น"
      // ถ้าปล่อยให้เด้ง Permission Denied รายการที่อยู่ข้างหลังจะหายไปทั้งหน้า
      meta: { suppressForbiddenRedirect: true },
    },
  );
};

export const publishPurchaseOrders = (body: PurchaseOrderPublishRequest) => {
  return axios.post<ApiResponse<PurchaseOrderPublishData>>(
    `/purchase-orders/publish`,
    body,
    {
      // ไม่ตั้ง successMsg: จำนวนที่ส่งไปต่อสาขาอยู่ใน response จอ W5 จึงประกาศเอง
      // ด้วย pushGlobalSuccess หลังอ่านค่าที่ได้กลับมา (แบบเดียวกับ confirm แผนวิ่ง)
      // 403 = ไม่ใช่ HO เป็นผลของปุ่มใน dialog ไม่ใช่ห้ามเข้าหน้า
      meta: { suppressForbiddenRedirect: true },
    },
  );
};

export const getPurchaseOrderCustomerCandidates = (
  uuid: string,
  q?: string,
) => {
  const query = new URLSearchParams();
  if (q) query.append("q", q);
  const search = query.toString();

  return axios.get<ApiResponse<PurchaseOrderCustomerCandidatesData>>(
    `/purchase-orders/${uuid}/customer-candidates${search ? `?${search}` : ""}`,
    {
      // 403 = PO ของสาขาอื่น · dropdown จับคู่ร้านเปิดคร่อมตาราง W7 อยู่
      meta: { suppressForbiddenRedirect: true },
    },
  );
};

export const setPurchaseOrderCustomer = (
  uuid: string,
  customerUuid: string,
  publish = false,
) => {
  return axios.patch<ApiResponse<PurchaseOrderListItem>>(
    `/purchase-orders/${uuid}/customer`,
    // publish=true คือ "บันทึกและส่งสาขา" ของ HO · ไม่ส่งคีย์เมื่อไม่ได้ส่งสาขา
    publish ? { customer_uuid: customerUuid, publish } : { customer_uuid: customerUuid },
    {
      meta: {
        successMsg: publish ? "จับคู่ร้านและส่งให้สาขาแล้ว" : "จับคู่ร้านแล้ว",
        // 403 (PO หรือร้านของสาขาอื่น) เป็นผลของปุ่มบันทึกรายแถว ไม่ใช่ห้ามเข้าหน้า
        suppressForbiddenRedirect: true,
      },
    },
  );
};

// ระบุสาขาให้ PO ที่ยังไม่มีสาขา (HO) · ไม่ตั้ง successMsg เพราะจำนวนที่ระบุได้อยู่ใน
// response จอจึงประกาศเอง · 409 (มีใบที่ใช้แล้ว/ส่งไปแล้ว) มีข้อความไทยจาก backend
// ที่ interceptor แสดงให้ตามปกติ
export const setPurchaseOrdersBranch = (body: PurchaseOrderSetBranchRequest) => {
  return axios.patch<ApiResponse<PurchaseOrderSetBranchData>>(
    `/purchase-orders/branch`,
    body,
    {
      // 403 = ไม่ใช่ HO เป็นผลของปุ่มใน dialog ไม่ใช่ห้ามเข้าหน้า
      meta: { suppressForbiddenRedirect: true },
    },
  );
};

// ส่งคืน HO (สาขา) · 409 (ใบที่ใช้แล้ว/หมดช่วงใช้/ยังไม่ส่งมา/ของสาขาอื่น) มีข้อความไทย
// จาก backend ที่ interceptor แสดงให้ตามปกติ · ไม่ตั้ง successMsg เพราะจำนวนที่ส่งคืนได้
// จริงอยู่ใน response (returned_count) จอจึงประกาศเอง
export const returnPurchaseOrders = (body: PurchaseOrderReturnRequest) => {
  return axios.post<ApiResponse<PurchaseOrderReturnData>>(
    `/purchase-orders/return`,
    body,
    {
      // 403 = ไม่ใช่ผู้ใช้สาขา เป็นผลของปุ่มใน dialog ไม่ใช่ห้ามเข้าหน้า
      meta: { suppressForbiddenRedirect: true },
    },
  );
};

// ดึงกลับจากสาขา (HO) · จำนวนที่ดึงกลับได้อยู่ใน response จอจึงประกาศเอง
export const unpublishPurchaseOrders = (body: PurchaseOrderUnpublishRequest) => {
  return axios.post<ApiResponse<PurchaseOrderUnpublishData>>(
    `/purchase-orders/unpublish`,
    body,
    { meta: { suppressForbiddenRedirect: true } },
  );
};

export const getPurchaseOrderWeeklySummary = (
  params: PurchaseOrderWeeklySummaryParams,
) => {
  const query = new URLSearchParams();
  query.append("week_start", params.week_start);
  if (params.company_id) query.append("company_id", params.company_id);
  if (params.chain) query.append("chain", params.chain);

  return axios.get<ApiResponse<PurchaseOrderWeeklySummaryData>>(
    `/purchase-orders/weekly-summary?${query.toString()}`,
  );
};

export const exportPurchaseOrders = (filter: PurchaseOrderListFilter) => {
  const query = buildPurchaseOrderFilterQuery(filter);

  return axios.get<Blob>(`/purchase-orders/export?${query.toString()}`, {
    responseType: "blob",
    meta: { suppressForbiddenRedirect: true },
  });
};

// ---- Formula Scorer (dashboard: recommend + human confirm) ----
// company_id is derived from the JWT on the backend — never sent from here.

export const getFormulaScorerPreview = (body: FormulaScorerPreviewRequest) => {
  return axios.post<ApiResponse<FormulaScorerPreviewResponse>>(
    "/formula-scorer/preview",
    body,
  );
};

export const confirmFormula = (body: FormulaDecisionRequest) => {
  return axios.post<ApiResponse<FormulaDecisionResult>>(
    "/formula-scorer/confirm",
    body,
    { meta: { successMsg: "ยืนยันสลับสูตรแล้ว" } },
  );
};

export const rejectFormula = (body: FormulaDecisionRequest) => {
  return axios.post<ApiResponse<FormulaDecisionResult>>(
    "/formula-scorer/reject",
    body,
    { meta: { successMsg: "บันทึกว่าไม่สลับสูตรแล้ว" } },
  );
};

export const overrideFormula = (body: FormulaDecisionRequest) => {
  return axios.post<ApiResponse<FormulaDecisionResult>>(
    "/formula-scorer/override",
    body,
    { meta: { successMsg: "สลับสูตรด้วยตนเองแล้ว" } },
  );
};

/**
 * แดชบอร์ด QR — the channel split behind the ขายสด bill population.
 *
 * `branch_code` narrows nothing for a branch-scoped caller: the server resolves tenancy from
 * the JWT and ignores the parameter for everyone but head office and master. Sent anyway so
 * one call site serves both cohorts.
 *
 * Empty filters are omitted rather than sent blank — the server validates what it is given,
 * and an empty string is a value, not an absence.
 */
const qrAnalyticsSearch = (params: {
  date_from: string;
  date_to: string;
  branch_code?: string;
  route_code?: string;
}) => {
  const search = new URLSearchParams();
  search.append("date_from", params.date_from);
  search.append("date_to", params.date_to);
  for (const key of ["branch_code", "route_code"] as const) {
    const value = params[key];
    if (value) search.append(key, value);
  }
  return search;
};

export const getQrAnalyticsSummary = (params: {
  date_from: string;
  date_to: string;
  branch_code?: string;
  route_code?: string;
}) => {
  return axios.get<ApiResponse<QrAnalyticsSummaryResponse>>(
    `/qr-analytics/summary?${qrAnalyticsSearch(params).toString()}`,
  );
};

export const getQrAnalyticsTrend = (params: {
  date_from: string;
  date_to: string;
  group_by: QrTrendGroupBy;
  branch_code?: string;
  route_code?: string;
}) => {
  const search = qrAnalyticsSearch(params);
  search.append("group_by", params.group_by);
  return axios.get<ApiResponse<QrAnalyticsTrendResponse>>(
    `/qr-analytics/trend?${search.toString()}`,
  );
};

/** One endpoint serves both rankings — `sort_dir: "asc"` is the "สแกนน้อยที่สุด" list. */
export const getQrAnalyticsShops = (params: {
  date_from: string;
  date_to: string;
  sort_dir: QrShopSortDir;
  sort_by?: QrShopSortBy;
  page?: number;
  limit?: number;
  branch_code?: string;
  route_code?: string;
}) => {
  const search = qrAnalyticsSearch(params);
  search.append("sort_dir", params.sort_dir);
  search.append("sort_by", params.sort_by ?? "qr_count");
  search.append("page", String(params.page ?? 1));
  search.append("limit", String(params.limit ?? 10));
  return axios.get<ApiResponse<QrShopRankResponse>>(
    `/qr-analytics/shops?${search.toString()}`,
  );
};

/**
 * สรุปรอบเข้าเยี่ยมร้าน. Empty filters are omitted rather than sent blank, and `holidays`
 * travels as one comma-separated value. `branch_code` is honoured for head office only — any
 * other caller is pinned to its own branch by the server.
 */
const visitFrequencySearch = (
  params: VisitFrequencySummaryParams & Partial<VisitFrequencyCustomerParams>,
) => {
  const search = new URLSearchParams();
  search.append("date_from", params.date_from);
  search.append("date_to", params.date_to);
  if (params.holidays && params.holidays.length > 0) {
    search.append("holidays", params.holidays.join(","));
  }
  for (const key of ["branch_code", "route_number", "bucket", "search"] as const) {
    const value = params[key];
    if (value) search.append(key, value);
  }
  if (params.page !== undefined) search.append("page", String(params.page));
  if (params.limit !== undefined) search.append("limit", String(params.limit));
  return search;
};

export const getVisitFrequencySummary = (params: VisitFrequencySummaryParams) =>
  axios.get<ApiResponse<VisitFrequencySummaryResponse>>(
    `/dashboard/visit-frequency?${visitFrequencySearch(params).toString()}`,
  );

export const getVisitFrequencyCustomers = (params: VisitFrequencyCustomerParams) =>
  axios.get<ApiResponse<VisitFrequencyCustomerResponse>>(
    `/dashboard/visit-frequency/customers?${visitFrequencySearch(params).toString()}`,
  );

const INACTIVE_STORES_API = "/dashboard/inactive-stores";

/**
 * ร้านค้าไม่ได้เข้านาน. Empty filters are omitted rather than sent blank. `branch_code` is
 * honoured for head office only — any other caller is pinned to its own branch by the server.
 */
const inactiveStoreSearch = (
  params: Omit<InactiveStoreFilterParams, "days"> &
    InactiveStoreSummaryParams &
    Partial<Pick<InactiveStoreListParams, "state" | "page" | "limit">> &
    Partial<Pick<InactiveStoreExportParams, "format">>,
) => {
  const search = new URLSearchParams();
  if (params.days !== undefined) search.append("days", String(params.days));
  for (const key of ["branch_code", "van", "shop_group", "search", "state", "format"] as const) {
    const value = params[key];
    if (value) search.append(key, value);
  }
  if (params.page !== undefined) search.append("page", String(params.page));
  if (params.limit !== undefined) search.append("limit", String(params.limit));
  return search;
};

export const getInactiveStoreSummary = (params: InactiveStoreSummaryParams) =>
  axios.get<ApiResponse<InactiveStoreSummaryResponse>>(
    `${INACTIVE_STORES_API}?${inactiveStoreSearch(params).toString()}`,
  );

export const getInactiveStores = (params: InactiveStoreListParams) =>
  axios.get<ApiResponse<InactiveStorePageResponse>>(
    `${INACTIVE_STORES_API}/stores?${inactiveStoreSearch(params).toString()}`,
  );

/**
 * A 403 here is about the filter (e.g. a stale branch), not the page, so it surfaces as the
 * usual error toast instead of replacing the report with the Permission Denied screen.
 */
export const exportInactiveStores = (params: InactiveStoreExportParams) =>
  axios.get<Blob>(`${INACTIVE_STORES_API}/export?${inactiveStoreSearch(params).toString()}`, {
    responseType: "blob",
    meta: { suppressForbiddenRedirect: true },
  });

// ---- สรุปถาดขึ้น–ถาดกลับ — one call per level: every branch → one branch's routes → one route's trips,
// and the หมายเหตุ a branch writes on a trip ----

const PALLET_SUMMARY_API = "/pallet-summary";

const palletSummarySearch = ({ start_date, end_date }: PalletSummaryDateRange) =>
  new URLSearchParams({ start_date, end_date });

/** Head office and master only; the server answers 403 to everyone else. */
export const getPalletSummaryBranches = (params: PalletSummaryDateRange) =>
  axios.get<ApiResponse<PalletBranchSummary>>(
    `${PALLET_SUMMARY_API}/branches?${palletSummarySearch(params).toString()}`,
  );

/** `branch_code` is left out for a branch reader: the server pins them to their own branch. */
export const getPalletSummaryRoutes = (params: PalletRouteSummaryParams) => {
  const search = palletSummarySearch(params);
  if (params.branch_code) search.append("branch_code", params.branch_code);
  return axios.get<ApiResponse<PalletRouteSummary>>(
    `${PALLET_SUMMARY_API}/routes?${search.toString()}`,
  );
};

export const getPalletSummaryTrips = (params: PalletTripSummaryParams) => {
  const search = palletSummarySearch(params);
  search.append("route_uuid", params.route_uuid);
  return axios.get<ApiResponse<PalletTripSummary>>(
    `${PALLET_SUMMARY_API}/trips?${search.toString()}`,
  );
};

/**
 * A trip's หมายเหตุ, written by the branch roles of its own branch; null clears it. A refusal is
 * about the write, not the page, so it is a toast rather than the Permission Denied screen.
 */
export const updatePalletTripNote = (checkinUuid: string, note: string | null) =>
  axios.put<ApiResponse<PalletTripNoteResult>>(
    `${PALLET_SUMMARY_API}/trips/${encodeURIComponent(checkinUuid)}/note`,
    { note },
    { meta: { successMsg: "บันทึกหมายเหตุสำเร็จ", suppressForbiddenRedirect: true } },
  );

// ---- สรุปเปอร์เซ็นต์ของขึ้น ของเสีย — one route, or every route of the branch when route_uuid is left out ----

const WASTE_PERCENT_REPORT_API = "/waste-percent-report";

/** `branch_code` is left out for a branch reader: the server pins them to their own branch. */
export const getWastePercentReport = (params: WastePercentReportParams) => {
  const search = new URLSearchParams({ month: params.month });
  if (params.branch_code) search.append("branch_code", params.branch_code);
  if (params.route_uuid) search.append("route_uuid", params.route_uuid);
  return axios.get<ApiResponse<WastePercentReport>>(
    `${WASTE_PERCENT_REPORT_API}?${search.toString()}`,
  );
};

// ---- ออเดอร์พิเศษ (special order) — special-order-api-contract.md v1 ----

const SPECIAL_ORDERS_API = "/special-orders";

/**
 * Every active line of the customer's pricebook, the customer's billing terms and the two
 * routes an order for it may be booked on (its own MAIN route and its branch's SPECIAL one).
 * Lines are priced for `deliveryDate` (`YYYY-MM-DD`), the day the draft is snapshotted at, so a
 * price period covering that day shows; omitted, the API prices for today.
 */
export const getSpecialOrderPriceList = (
  customerUuid: string,
  deliveryDate?: string,
) => {
  const query = new URLSearchParams({ customer_uuid: customerUuid });
  if (deliveryDate) query.append("delivery_date", deliveryDate);
  return axios.get<ApiResponse<SpecialOrderPriceList>>(
    `${SPECIAL_ORDERS_API}/price-list?${query.toString()}`,
  );
};

export const getSpecialOrders = (params: SpecialOrderListParams) => {
  const search = new URLSearchParams();
  search.append("page", String(params.page));
  search.append("limit", String(params.limit));
  const optional = [
    "status",
    "delivery_date_from",
    "delivery_date_to",
    "route_uuid",
    "customer_uuid",
    "q",
    "company_id",
  ] as const;
  for (const key of optional) {
    const value = params[key];
    if (value) search.append(key, value);
  }
  return axios.get<ApiResponse<SpecialOrderListData>>(
    `${SPECIAL_ORDERS_API}?${search.toString()}`,
  );
};

export const getSpecialOrder = (uuid: string) =>
  axios.get<ApiResponse<SpecialOrderDetail>>(
    `${SPECIAL_ORDERS_API}/${encodeURIComponent(uuid)}`,
  );

export const createSpecialOrder = (body: SpecialOrderCreateRequest) =>
  axios.post<ApiResponse<SpecialOrderDetail>>(SPECIAL_ORDERS_API, body, {
    meta: { successMsg: "บันทึกร่างออเดอร์พิเศษสำเร็จ" },
  });

/** Draft only — any other status answers 409 SPECIAL_ORDER_INVALID_STATUS. */
export const updateSpecialOrder = (
  uuid: string,
  body: SpecialOrderUpdateRequest,
) =>
  axios.patch<ApiResponse<SpecialOrderDetail>>(
    `${SPECIAL_ORDERS_API}/${encodeURIComponent(uuid)}`,
    body,
    { meta: { successMsg: "บันทึกร่างออเดอร์พิเศษสำเร็จ" } },
  );

/** draft → ordered: reserves the bill number and starts rendering the A4 set. */
export const confirmSpecialOrder = (uuid: string) =>
  axios.post<ApiResponse<SpecialOrderDetail>>(
    `${SPECIAL_ORDERS_API}/${encodeURIComponent(uuid)}/confirm`,
    {},
    { meta: { successMsg: "ยืนยันสั่งสำเร็จ" } },
  );

/**
 * ordered → delivered, web side (mode B: the customer collected at the branch). Only on the
 * delivery date — any other day is a 409 SPECIAL_ORDER_WRONG_DATE.
 */
export const deliverSpecialOrder = (
  uuid: string,
  body: SpecialOrderDeliverRequest,
) =>
  axios.post<ApiResponse<SpecialOrderDetail>>(
    `${SPECIAL_ORDERS_API}/${encodeURIComponent(uuid)}/deliver`,
    body,
    { meta: { successMsg: "ยืนยันส่งสำเร็จ" } },
  );

/**
 * Cancels a draft/ordered order, or — once delivered — the bill itself (stock reverted).
 * The caller shows the refusal (e.g. the eTax guard's 409) inside its own dialog, so the
 * generic toast is suppressed.
 */
export const cancelSpecialOrder = (
  uuid: string,
  body: SpecialOrderCancelRequest,
) =>
  axios.post<ApiResponse<SpecialOrderDetail>>(
    `${SPECIAL_ORDERS_API}/${encodeURIComponent(uuid)}/cancel`,
    body,
    {
      meta: {
        successMsg: "ยกเลิกออเดอร์พิเศษสำเร็จ",
        suppressErrorToast: true,
        suppressForbiddenRedirect: true,
      },
    },
  );

export const SPECIAL_ORDER_DOCUMENT_ERROR_MSG_BY_STATUS: Record<number, string> = {
  404: "ไม่พบเอกสารของออเดอร์นี้",
  409: "ออเดอร์ยังเป็นร่าง ยังไม่มีเอกสาร",
  502: "สร้างเอกสารไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
};

/** The A4 set as a PDF stream — `customer` = sheets 1–3, `all` = sheets 1–6. */
export const downloadSpecialOrderDocuments = (
  uuid: string,
  sheets: SpecialOrderDocumentSheets,
) =>
  axios.get<Blob>(
    `${SPECIAL_ORDERS_API}/${encodeURIComponent(uuid)}/documents?sheets=${sheets}`,
    {
      responseType: "blob",
      meta: {
        errorMsgByStatus: SPECIAL_ORDER_DOCUMENT_ERROR_MSG_BY_STATUS,
        suppressForbiddenRedirect: true,
      },
    },
  );

/**
 * The empty A4 order form for one customer (header filled, product lines blank) that the
 * van writes on by hand when the device is down — a PDF stream like the order documents.
 */
export const downloadSpecialOrderBlankForm = (customerUuid: string) =>
  axios.get<Blob>(
    `${SPECIAL_ORDERS_API}/blank-form?customer_uuid=${encodeURIComponent(customerUuid)}`,
    {
      responseType: "blob",
      meta: {
        errorMsgByStatus: {
          404: "ไม่พบข้อมูลลูกค้า",
          502: "สร้างฟอร์มไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
        },
        suppressForbiddenRedirect: true,
      },
    },
  );

/** The day-close panel: each SPECIAL route's order counts for one day. */
export const getSpecialRoutesPending = (date: string, companyId?: string) => {
  const search = new URLSearchParams();
  search.append("date", date);
  if (companyId) search.append("company_id", companyId);
  return axios.get<ApiResponse<SpecialRoutePending[]>>(
    `/special-routes/pending?${search.toString()}`,
  );
};

/**
 * Submits the special route's day (reconcile in == out + the cash sale-deposit notice).
 * A 409 SPECIAL_ORDER_PENDING_PICKUP carries the orders nobody collected yet; the caller
 * lists them, so the toast is left to the interceptor only for the message.
 */
export const closeSpecialRouteDay = (body: SpecialRouteCloseDayRequest) =>
  axios.post<ApiResponse<SpecialRouteCloseDayResult>>(
    "/special-routes/close-day",
    body,
    {
      meta: {
        successMsg: "ปิดวันสายพิเศษสำเร็จ",
        suppressForbiddenRedirect: true,
      },
    },
  );

// ---- Demand Factor (demand_adjustments) ----

const DEMAND_ADJUSTMENTS_API = "/demand-adjustments";

const DEMAND_ADJUSTMENT_LIST_FILTERS = [
  "type",
  "status",
  "date",
  "valid_from",
  "valid_to",
  "branch_code",
] as const;

export const getDemandAdjustments = (params: DemandAdjustmentListParams) => {
  const search = new URLSearchParams();
  search.append("page", String(params.page));
  search.append("limit", String(params.limit));
  for (const key of DEMAND_ADJUSTMENT_LIST_FILTERS) {
    const value = params[key];
    if (value) search.append(key, value);
  }
  return axios.get<ApiResponse<DemandAdjustmentListData>>(
    `${DEMAND_ADJUSTMENTS_API}?${search.toString()}`,
  );
};

// A refused save or delete (e.g. a row outside the manager's branch) is an action-level 403:
// the page stays and the global toast names the refusal.
export const createDemandAdjustment = (body: DemandAdjustmentWriteRequest) =>
  axios.post<ApiResponse<DemandAdjustment>>(DEMAND_ADJUSTMENTS_API, body, {
    meta: {
      successMsg: "เพิ่ม Demand Factor สำเร็จ",
      suppressForbiddenRedirect: true,
    },
  });

export const updateDemandAdjustment = (
  id: number,
  body: DemandAdjustmentWriteRequest,
) =>
  axios.patch<ApiResponse<DemandAdjustment>>(
    `${DEMAND_ADJUSTMENTS_API}/${id}`,
    body,
    {
      meta: {
        successMsg: "แก้ไข Demand Factor สำเร็จ",
        suppressForbiddenRedirect: true,
      },
    },
  );

export const deleteDemandAdjustment = (id: number) =>
  axios.delete<ApiResponse<boolean>>(`${DEMAND_ADJUSTMENTS_API}/${id}`, {
    meta: {
      successMsg: "ลบ Demand Factor สำเร็จ",
      suppressForbiddenRedirect: true,
    },
  });

/** The rows that reach one store on one date and the multiplier each product ends up with. */
export const previewDemandAdjustment = (body: DemandAdjustmentPreviewRequest) =>
  axios.post<ApiResponse<DemandAdjustmentPreview>>(
    `${DEMAND_ADJUSTMENTS_API}/preview`,
    body,
    { meta: { suppressForbiddenRedirect: true } },
  );

// ---- ตั้งค่าระบบ (/system-settings) ----
// Every role reads the list (screens borrow values from it); only MASTER writes or reads history.

export const getSystemSettings = () =>
  axios.get<ApiResponse<SystemSettingListResponse>>("/system-settings");

export const updateSystemSetting = (key: string, body: UpdateSystemSettingRequest) =>
  axios.patch<ApiResponse<SystemSetting>>(
    `/system-settings/${encodeURIComponent(key)}`,
    body,
    { meta: { successMsg: "บันทึกการตั้งค่าแล้ว" } },
  );

export const getSystemSettingHistory = (key: string, page: number, limit: number) => {
  const search = new URLSearchParams();
  search.append("page", String(page));
  search.append("limit", String(limit));
  return axios.get<ApiResponse<SystemSettingHistoryResponse>>(
    `/system-settings/${encodeURIComponent(key)}/history?${search.toString()}`,
  );
};

// ---- BDC (7-Eleven DC) เฟส 1 — /bdc ----
// นำเข้าไฟล์ EDI (ORDS + PICKLST) เป็น 2 ขั้นเหมือน PO: preview ไม่เขียน DB (พักผลตรวจ 30 นาที)
// แล้ว confirm เขียนจากผลที่พักไว้ · ผู้ใช้สาขาเข้าได้เฉพาะเมื่อสาขาของ token คือสาขา BDC
// (backend ตอบ 403 ให้) จึงปล่อย 403 ของ GET ระดับหน้าให้ไป Permission Denied ตามปกติ

export const previewBdcImport = (files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => {
    // ไฟล์ EDI เป็น text (TIS-620) · ส่งชื่อไฟล์เสมอ backend แยก ORDS / PICKLST จากชื่อ
    formData.append("files", new Blob([file], { type: "text/plain" }), file.name);
  });
  return axios.post<ApiResponse<BdcImportPreviewData>>(`/bdc/import/preview`, formData, {
    meta: { suppressForbiddenRedirect: true, suppressErrorToast: true },
  });
};

/** The confirm owns its error too (422 DC ยังไม่ผูก / 502 เก็บไฟล์ไม่สำเร็จ need an action by the button). */
export const confirmBdcImport = (uploadId: string) =>
  axios.post<ApiResponse<BdcImportConfirmData>>(
    `/bdc/import/${encodeURIComponent(uploadId)}/confirm`,
    {},
    { meta: { suppressForbiddenRedirect: true, suppressErrorToast: true } },
  );

const buildBdcShipmentQuery = (filter: BdcShipmentFilter) => {
  const query = new URLSearchParams();
  if (filter.order_date) query.append("order_date", filter.order_date);
  if (filter.delivery_date) query.append("delivery_date", filter.delivery_date);
  if (filter.dc_code) query.append("dc_code", filter.dc_code);
  if (filter.round !== undefined) query.append("round", String(filter.round));
  if (filter.kind) query.append("kind", filter.kind);
  if (filter.status) query.append("status", filter.status);
  if (filter.company_id) query.append("company_id", filter.company_id);
  return query;
};

export const listBdcShipments = (params: BdcShipmentListParams) => {
  const query = buildBdcShipmentQuery(params);
  query.append("page", String(params.page));
  query.append("limit", String(params.limit));
  return axios.get<ApiResponse<BdcShipmentListData>>(`/bdc/shipments?${query.toString()}`);
};

export const exportBdcShipments = (filter: BdcShipmentFilter) =>
  axios.get<Blob>(`/bdc/shipments/export?${buildBdcShipmentQuery(filter).toString()}`, {
    responseType: "blob",
    meta: { suppressForbiddenRedirect: true },
  });

export const getBdcShipment = (poNumber: string) =>
  axios.get<ApiResponse<BdcShipmentDetail>>(`/bdc/shipments/${encodeURIComponent(poNumber)}`);

export const exportBdcShipment = (poNumber: string, type: BdcShipmentExportType) =>
  axios.get<Blob>(
    `/bdc/shipments/${encodeURIComponent(poNumber)}/export?type=${type}`,
    { responseType: "blob", meta: { suppressForbiddenRedirect: true } },
  );

/** ใบคุมสินค้าทั้งวัน (หนึ่งชีตต่อ DC) ตามวันที่สั่ง · company_id = เฉพาะสาขา BDC นั้น */
export const exportBdcControlSheet = (orderDate: string, companyId?: string) => {
  const query = new URLSearchParams();
  query.append("order_date", orderDate);
  if (companyId) query.append("company_id", companyId);
  return axios.get<Blob>(`/bdc/export/control-sheet?${query.toString()}`, {
    responseType: "blob",
    meta: { suppressForbiddenRedirect: true },
  });
};

/** สาขา BDC ที่ผู้ใช้เห็น: HO/MASTER ทุกสาขาที่ลงทะเบียน · หัวหน้า BDC ทั้งสอง · เจ้าหน้าที่ BDC สาขาตัวเอง */
export const listBdcBranches = () =>
  axios.get<ApiResponse<BdcListShape<BdcBranch>>>(`/bdc/branches`);

/** ลงทะเบียนสาขา GDP เป็นสาขา BDC หรือเปิด/ปิดโหมดทดสอบ — MASTER เท่านั้น */
export const upsertBdcBranch = (companyId: string, body: BdcBranchUpsertRequest) =>
  axios.put<ApiResponse<BdcBranch>>(`/bdc/branches/${encodeURIComponent(companyId)}`, body, {
    meta: { successMsg: "บันทึกสาขา BDC แล้ว", suppressForbiddenRedirect: true },
  });

export const listBdcDcs = () => axios.get<ApiResponse<BdcListShape<BdcDc>>>(`/bdc/dcs`);

/** แก้ชื่อ DC และ/หรือผูก DC กับสาขา BDC (company_id null = ยกเลิกการผูก) */
export const updateBdcDc = (dcCode: string, body: BdcDcUpdateRequest) =>
  axios.patch<ApiResponse<BdcDc>>(`/bdc/dcs/${encodeURIComponent(dcCode)}`, body, {
    meta: { successMsg: "บันทึก DC แล้ว", suppressForbiddenRedirect: true },
  });

export const listBdcStores = (params: BdcStoreListParams) => {
  const query = new URLSearchParams();
  if (params.search) query.append("search", params.search);
  if (params.dc_code) query.append("dc_code", params.dc_code);
  query.append("page", String(params.page));
  query.append("limit", String(params.limit));
  return axios.get<ApiResponse<BdcStoreListData>>(`/bdc/stores?${query.toString()}`);
};

export const listBdcSkus = () => axios.get<ApiResponse<BdcListShape<BdcSku>>>(`/bdc/skus`);

export const updateBdcSku = (barcode: string, body: BdcSkuUpdateRequest) =>
  axios.patch<ApiResponse<BdcSku>>(`/bdc/skus/${encodeURIComponent(barcode)}`, body, {
    meta: { successMsg: "บันทึกสินค้าแล้ว", suppressForbiddenRedirect: true },
  });

/** ชิ้นต่อถาดของใบจ่ายสินค้า (1–1000) — แยกจากชิ้นต่อลังของใบคุม */
export const updateBdcSkuPcsPerTray = (barcode: string, pcsPerTray: number) =>
  axios.put<ApiResponse<BdcSkuTray>>(
    `/bdc/skus/${encodeURIComponent(barcode)}/pcs-per-tray`,
    { pcs_per_tray: pcsPerTray },
    { meta: { successMsg: "บันทึกชิ้นต่อถาดแล้ว", suppressForbiddenRedirect: true } },
  );

// ---- BDC ขาด-เกิน (stock day) — หนึ่งวันสั่งต่อสาขา BDC ----
// company_id ต้องส่งเมื่อผู้ใช้เห็นมากกว่าหนึ่งสาขา (ไม่ส่ง = 400) · แก้/ปิดวัน: หัวหน้า BDC + MASTER
// · เปิดวันใหม่: MASTER

const stockDayPath = (workDate: string) => `/bdc/stock-days/${encodeURIComponent(workDate)}`;

const withCompany = (companyId?: string): Record<string, string> =>
  companyId ? { company_id: companyId } : {};

export const getBdcStockDay = (workDate: string, companyId?: string) => {
  const query = new URLSearchParams(withCompany(companyId));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return axios.get<ApiResponse<BdcStockDay>>(`${stockDayPath(workDate)}${suffix}`);
};

/**
 * บันทึกเฉพาะช่องที่ส่ง · null = กลับไปใช้ค่าระบบ · ตอบเป็นวันทั้งวันที่คำนวณใหม่แล้ว
 * 400 รับโอน/โอนออกไม่มีหมายเหตุ: message ระบุชื่อสินค้า (barcode) มาแล้ว — ปล่อยให้ global alert แสดงตามนั้น
 */
export const saveBdcStockDay = (workDate: string, body: BdcStockDaySaveRequest) =>
  axios.put<ApiResponse<BdcStockDay>>(stockDayPath(workDate), body, {
    meta: { successMsg: "บันทึกขาด-เกินแล้ว", suppressForbiddenRedirect: true },
  });

export const closeBdcStockDay = (workDate: string, body: BdcStockDayCloseRequest) =>
  axios.post<ApiResponse<BdcStockDay>>(`${stockDayPath(workDate)}/close`, body, {
    meta: { successMsg: "ปิดวันแล้ว", suppressForbiddenRedirect: true },
  });

export const reopenBdcStockDay = (workDate: string, companyId?: string) =>
  axios.post<ApiResponse<BdcStockDay>>(`${stockDayPath(workDate)}/reopen`, withCompany(companyId), {
    meta: { successMsg: "เปิดวันใหม่แล้ว", suppressForbiddenRedirect: true },
  });

/** แบบฟอร์มแสดงยอด ขาด - เกิน (xlsx หน้าตาเดิม) */
export const exportBdcStockDay = (workDate: string, companyId?: string) => {
  const query = new URLSearchParams({ work_date: workDate, ...withCompany(companyId) });
  return axios.get<Blob>(`/bdc/stock-days/export?${query.toString()}`, {
    responseType: "blob",
    meta: { suppressForbiddenRedirect: true },
  });
};

/** แบบฟอร์มแสดงยอด ขาด - เกิน — PDF A4 แนวตั้งสำหรับพิมพ์ */
export const printBdcStockDay = (workDate: string, companyId?: string) => {
  const query = new URLSearchParams({ work_date: workDate, ...withCompany(companyId) });
  return axios.get<Blob>(`/bdc/stock-days/print?${query.toString()}`, {
    responseType: "blob",
    meta: {
      suppressForbiddenRedirect: true,
      errorMsgByStatus: {
        502: "ระบบสร้าง PDF ตอบช้าเกินไป กรุณาลองใหม่อีกครั้ง",
        503: "ระบบสร้าง PDF ไม่พร้อมใช้งาน กรุณาลองใหม่ภายหลัง",
      },
    },
  });
};

/** รายงาน GM ตามช่วงวัน (ไม่เกิน 92 วัน) — นับเฉพาะวันที่ปิดแล้ว */
export const getBdcStockReport = (dateFrom: string, dateTo: string, companyId?: string) => {
  const query = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, ...withCompany(companyId) });
  return axios.get<ApiResponse<BdcStockReport>>(`/bdc/stock-days/report?${query.toString()}`);
};

// ---- BDC ใบจ่ายสินค้า (dispatch note) — หนึ่งใบต่อ DC ต่อวันสั่ง ----
// company_id ต้องส่งเมื่อผู้ใช้เห็นมากกว่าหนึ่งสาขา · พิมพ์/บันทึก: MASTER + หัวหน้า/เจ้าหน้าที่ BDC

export const getBdcDispatchDay = (workDate: string, companyId?: string) => {
  const query = new URLSearchParams({ work_date: workDate, ...withCompany(companyId) });
  return axios.get<ApiResponse<BdcDispatchDay>>(`/bdc/dispatch-notes?${query.toString()}`);
};

/**
 * PDF ของ DC ที่เลือก (ไม่ส่ง dc_codes = ทุก DC ของวัน) · การพิมพ์ครั้งแรกสร้างใบจ่าย.
 * ตอบเป็น blob — อ่าน error body ไม่ได้ จึงกำหนดข้อความเองตาม status
 */
export const printBdcDispatchNotes = (body: BdcDispatchPrintRequest) =>
  axios.post<Blob>("/bdc/dispatch-notes/print", body, {
    responseType: "blob",
    meta: {
      suppressForbiddenRedirect: true,
      errorMsgByStatus: {
        400: "ข้อมูลที่ส่งพิมพ์ไม่ถูกต้อง",
        403: "ไม่มีสิทธิ์พิมพ์ใบจ่ายของสาขานี้",
        422: "DC ที่เลือกไม่มียอดเบิกในวันนี้ — โหลดรายการใหม่แล้วลองอีกครั้ง",
        502: "ระบบสร้าง PDF ตอบช้าเกินไป กรุณาลองใหม่อีกครั้ง",
        503: "ระบบสร้าง PDF ไม่พร้อมใช้งาน กรุณาลองใหม่ภายหลัง",
      },
    },
  });

export const getBdcDispatchNote = (uuid: string) =>
  axios.get<ApiResponse<BdcDispatchNote>>(`/bdc/dispatch-notes/${encodeURIComponent(uuid)}`);

/** บันทึกใบจ่ายกลับ — ส่งเฉพาะช่องที่แก้ · null = ล้างค่า · ตอบเป็นใบทั้งใบ */
export const recordBdcDispatchNote = (uuid: string, body: BdcDispatchRecordRequest) =>
  axios.put<ApiResponse<BdcDispatchNote>>(`/bdc/dispatch-notes/${encodeURIComponent(uuid)}`, body, {
    meta: { successMsg: "บันทึกใบจ่ายแล้ว", suppressForbiddenRedirect: true },
  });

// ---- BDC เฟส 1.5 — ยอดส่ง / ยืนยัน DO (PR 3) ----
// ลดยอดส่งได้อย่างเดียว · แก้ได้เฉพาะ PO ที่เป็นร่าง (409 เมื่อยืนยัน DO แล้ว)

const bdcPoPath = (poNumber: string) => `/bdc/shipments/${encodeURIComponent(poNumber)}`;

/** เกลี่ยแบบ Auto — ดูตัวอย่าง ยังไม่บันทึก · requires_allow_zero = ต้องถามก่อนให้ร้านเหลือ 0 */
export const previewBdcAdjust = (poNumber: string, body: BdcAdjustPreviewRequest) =>
  axios.post<ApiResponse<BdcAdjustPreview>>(`${bdcPoPath(poNumber)}/adjust/preview`, body, {
    meta: { suppressForbiddenRedirect: true, suppressErrorToast: true },
  });

/** บันทึกยอดส่งรายร้าน (rows ของ preview = auto · แก้ในตาราง = manual) */
export const saveBdcDelivery = (poNumber: string, body: BdcDeliverySaveRequest) =>
  axios.put<ApiResponse<BdcDeliverySaveResponse>>(`${bdcPoPath(poNumber)}/delivery`, body, {
    meta: { successMsg: "บันทึกยอดส่งแล้ว", suppressForbiddenRedirect: true },
  });

export const confirmBdcDo = (poNumber: string) =>
  axios.post<ApiResponse<BdcShipmentStatusResponse>>(`${bdcPoPath(poNumber)}/confirm-do`, {}, {
    meta: { suppressForbiddenRedirect: true, suppressErrorToast: true },
  });

export const unconfirmBdcDo = (poNumber: string) =>
  axios.post<ApiResponse<BdcShipmentStatusResponse>>(`${bdcPoPath(poNumber)}/unconfirm-do`, {}, {
    meta: { successMsg: "ยกเลิกยืนยัน DO แล้ว — PO กลับเป็นร่าง", suppressForbiddenRedirect: true },
  });

export const listBdcAdjustments = (poNumber: string, page: number, limit: number) =>
  axios.get<ApiResponse<BdcQtyAdjustmentListData>>(
    `${bdcPoPath(poNumber)}/adjustments?page=${page}&limit=${limit}`,
  );

// ---- BDC เฟส 1.5 — INV, เลขรัน, ศูนย์เอกสาร, ทะเบียน INV (PR 5) ----
// 409 ของการออก INV มี error.code (BDC_INVOICE_*) — หน้าจอจัดการเอง จึงปิด toast กลาง

/** ออก INV — ต้องยืนยัน DO แล้ว · สาขาโหมดทดสอบได้เลข TB… */
export const issueBdcInvoice = (poNumber: string) =>
  axios.post<ApiResponse<BdcInvoiceActionResponse>>(`${bdcPoPath(poNumber)}/invoice`, {}, {
    meta: { suppressForbiddenRedirect: true, suppressErrorToast: true },
  });

export const listBdcPoInvoices = (poNumber: string) =>
  axios.get<ApiResponse<BdcInvoiceListData>>(`${bdcPoPath(poNumber)}/invoices`);

/** W12 — ราคาใน PO ที่ต่างจากราคา master BDC */
export const getBdcPriceCheck = (poNumber: string) =>
  axios.get<ApiResponse<BdcPriceCheck>>(`${bdcPoPath(poNumber)}/price-check`);

const buildBdcInvoiceQuery = (params: Omit<BdcInvoiceListParams, "page" | "limit">) => {
  const query = new URLSearchParams();
  if (params.date_from) query.append("date_from", params.date_from);
  if (params.date_to) query.append("date_to", params.date_to);
  if (params.status) query.append("status", params.status);
  if (params.dc_code) query.append("dc_code", params.dc_code);
  if (params.search) query.append("search", params.search);
  if (params.is_test !== undefined) query.append("is_test", String(params.is_test));
  if (params.company_id) query.append("company_id", params.company_id);
  return query;
};

/** ทะเบียน INV — รวมใบที่ยกเลิก · is_test = true แสดงชุดทดสอบ TB… แทน */
export const listBdcInvoices = (params: BdcInvoiceListParams) => {
  const query = buildBdcInvoiceQuery(params);
  query.append("page", String(params.page));
  query.append("limit", String(params.limit));
  return axios.get<ApiResponse<BdcInvoiceListData>>(`/bdc/invoices?${query.toString()}`);
};

/** Excel ทะเบียน INV — server ไม่ส่งใบทดสอบ TB… ในไฟล์นี้เลย */
export const exportBdcInvoices = (params: Omit<BdcInvoiceListParams, "page" | "limit">) =>
  axios.get<Blob>(`/bdc/invoices/export?${buildBdcInvoiceQuery({ ...params, is_test: undefined }).toString()}`, {
    responseType: "blob",
    meta: { suppressForbiddenRedirect: true },
  });

export const getBdcInvoice = (invoiceNo: string) =>
  axios.get<ApiResponse<BdcInvoiceDetail>>(`/bdc/invoices/${encodeURIComponent(invoiceNo)}`);

/** สำเนา INV ที่พิมพ์ต่อจากต้นฉบับ — ค่าเริ่มต้นและสูงสุด (ตรงกับ BE) */
export const BDC_INVOICE_DEFAULT_COPIES = 2;
export const BDC_INVOICE_MAX_COPIES = 5;

/** PDF ต้นฉบับ + สำเนา `copies` ฉบับ (DocGen) — ใบทดสอบมีลายน้ำ ใบยกเลิกมีตรายกเลิก */
export const getBdcInvoicePdf = (invoiceNo: string, copies: number = BDC_INVOICE_DEFAULT_COPIES) =>
  axios.get<Blob>(`/bdc/invoices/${encodeURIComponent(invoiceNo)}/pdf?copies=${copies}`, {
    responseType: "blob",
    meta: {
      suppressForbiddenRedirect: true,
      errorMsgByStatus: {
        404: "ไม่พบ INV นี้",
        502: "ระบบสร้างเอกสารตอบช้าเกินไป กรุณาลองใหม่อีกครั้ง",
        503: "ระบบสร้างเอกสารไม่พร้อมใช้งาน กรุณาลองใหม่ภายหลัง",
      },
    },
  });

/** ยกเลิก INV (ต้องมีเหตุผล) — เลขเดิมคงสถานะ "ยกเลิก" · PO กลับเป็นยืนยัน DO */
export const cancelBdcInvoice = (invoiceNo: string, reason: string) =>
  axios.post<ApiResponse<BdcInvoiceActionResponse>>(
    `/bdc/invoices/${encodeURIComponent(invoiceNo)}/cancel`,
    { reason },
    { meta: { successMsg: `ยกเลิก INV ${invoiceNo} แล้ว`, suppressForbiddenRedirect: true } },
  );

export const listBdcInvoiceSequences = () =>
  axios.get<ApiResponse<BdcListShape<BdcDocSequence>>>(`/bdc/invoice-sequence`);

export const listBdcInvoiceSequenceHistory = (page: number, limit: number, prefix?: string) => {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (prefix) query.append("prefix", prefix);
  return axios.get<ApiResponse<BdcDocSequenceHistoryData>>(`/bdc/invoice-sequence/history?${query.toString()}`);
};

/** Set เลขถัดไป — MASTER เท่านั้น · 409 BDC_INVOICE_NO_ALREADY_USED = เลขนั้นมีใน INV แล้ว */
/** P2: every series of `/bdc/invoice-sequence` (INV B / TB, CN C2 / TC2 …, book VY …) Sets the same way. */
export const setBdcInvoiceSequence = (prefix: string, nextNo: number) =>
  axios.post<ApiResponse<BdcDocSequence>>(
    `/bdc/invoice-sequence/set`,
    { prefix, next_no: nextNo },
    { meta: { suppressForbiddenRedirect: true, suppressErrorToast: true } },
  );

/** Reset กลับไปเริ่ม 1 — MASTER เท่านั้น · next_in_use บอกว่าเลข 1 เคยใช้แล้วหรือยัง */
export const resetBdcInvoiceSequence = (prefix: string) =>
  axios.post<ApiResponse<BdcDocSequence>>(
    `/bdc/invoice-sequence/reset`,
    { prefix },
    { meta: { successMsg: `Reset เลข ${prefix} แล้ว`, suppressForbiddenRedirect: true } },
  );

/** ศูนย์เอกสาร — shipment ของวันหนึ่ง (วันส่งหรือวันที่สั่ง) + เอกสารที่พิมพ์ได้ตอนนี้ */
export const listBdcDocuments = (params: BdcDocumentListParams) => {
  const query = new URLSearchParams();
  if (params.delivery_date) query.append("delivery_date", params.delivery_date);
  if (params.order_date) query.append("order_date", params.order_date);
  if (params.dc_code) query.append("dc_code", params.dc_code);
  if (params.company_id) query.append("company_id", params.company_id);
  return axios.get<ApiResponse<BdcListShape<BdcDocumentShipment>>>(`/bdc/documents?${query.toString()}`);
};

/** PDF ไฟล์เดียว (หลาย shipment × หลายเอกสาร) หรือ Excel ของเอกสารเดียว */
export const renderBdcDocuments = (body: BdcDocumentRenderRequest) =>
  axios.post<Blob>(`/bdc/documents`, body, {
    responseType: "blob",
    timeout: 180000,
    meta: {
      suppressForbiddenRedirect: true,
      errorMsgByStatus: {
        400: "Excel เลือกได้ครั้งละหนึ่งเอกสาร คือ ใบคุมสรุปตามสาย หรือ ใบคุมรายร้าน (INV ไม่มีแบบ Excel) · ใบส่งสินค้ารายร้าน (A5) ต้องพิมพ์แยกไฟล์ และไม่เกิน 3,000 ร้านต่อครั้ง",
        404: "มี shipment ที่ไม่พบ หรือไม่มีสิทธิ์ดู",
        422: "shipment และเอกสารที่เลือกไม่มีเอกสารที่พิมพ์ได้",
        502: "ระบบสร้างเอกสารตอบช้าเกินไป ลองเลือก shipment น้อยลงแล้วลองใหม่",
        503: "ระบบสร้างเอกสารไม่พร้อมใช้งาน กรุณาลองใหม่ภายหลัง",
      },
    },
  });

// ---- รายงานยอดขายทั้งบริษัท (HO) — BDC เฟส 1.5 PR 8 · อ่านจาก view รวม (L4) ----

const companySalesQuery = (params: CompanySalesReportParams) => {
  const query = new URLSearchParams();
  query.append("date_from", params.date_from);
  query.append("date_to", params.date_to);
  query.append("group_by", params.group_by);
  if (params.company_id) query.append("company_id", params.company_id);
  return query.toString();
};

/** ยอดขายสุทธิ (INV − CN, ก่อน VAT) ต่อสาขา × วัน/เดือน แยก Vansale / BDC */
export const getCompanySalesReport = (params: CompanySalesReportParams) =>
  axios.get<ApiResponse<CompanySalesReport>>(`/reports/company-sales?${companySalesQuery(params)}`);

/** Excel ของรายงานเดียวกัน (แถวสาขา × งวด + แถวรวมทั้งหมด) */
export const exportCompanySalesReport = (params: CompanySalesReportParams) =>
  axios.get<Blob>(`/reports/company-sales/export?${companySalesQuery(params)}`, {
    responseType: "blob",
    meta: {
      errorMsgByStatus: {
        400: "ช่วงวันที่ไม่ถูกต้อง (เลือกได้ไม่เกิน 366 วัน)",
        422: "ข้อมูลมีมากเกินไป กรุณาเลือกช่วงวันที่ให้แคบลง",
      },
    },
  });
